import IdentificacaoFiscal from '../components/IdentificacaoFiscal';
import LiquidText from '../components/LiquidText';
import { OrcamentoIcon } from '../components/OrcamentoUI';
import { useLiquidFeedback, LiquidDialog } from '../components/LiquidUI';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import api from '../services/api';
import LoteMensagens from '../components/SuspeitasCombustivel';
import SinalCombustivel from '../components/SinalCombustivel';
import HistoricoEnvios from '../components/HistoricoEnvios';
import { useAuth } from '../context/AuthContext';
import CardManagerModal from '../components/CardManagerModal';
import Reconciliador from './Reconciliador';
import { fmtMoney } from '../utils/moeda';
import { useTardios, janelaPadraoDesde } from '../context/TardiosContext';
import './Despesas.css';

/* Em nome de quem foi passada a fatura. É o que decide se a empresa pode deduzir
   o IVA, e o ecrã dizia "Consumidor Final" a toda a gente porque lia
   `nif_utilizador`, propriedade que não existe em lado nenhum — a coluna
   chama-se `nif_adquirente`. Hoje há 1048 faturas em nome da SPROD que o modal
   dava como sendo de consumidor final. */
const NIF_SPROD = '514432233';

/* Hoje e o limite para trás, no fuso de quem está a usar. `toISOString()` dá
   UTC: às primeiras horas da noite devolvia o dia anterior, e a despesa nascia
   com a data de ontem. */
const diaISO = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString().split('T')[0];
const hojeISO = () => diaISO(new Date());
const limiteAntigoISO = () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 2);
    return diaISO(d);
};

/* O mês em que a despesa vai contar. É a única coisa que interessa saber ao
   escrever a data: é por ele que a fatura entra no fecho do mês e encontra (ou
   não) o movimento no extrato. */
const mesPorExtenso = (iso) => {
    if (!iso) return '';
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
};

/* A data escrita num talão português, lida do texto que o OCR devolve.
   Aceita 31-07-2026, 31/07/26, 2026-07-31 e as variantes com pontos — em
   Portugal o dia vem primeiro, e "07-08" é 7 de agosto e não 8 de julho.
   Devolve AAAA-MM-DD, ou '' quando não há nada de que se possa ter a certeza.

   Regras de prudência, porque uma data mal lida é pior do que data nenhuma:
   linhas de validade e de prazo de pagamento ficam de fora; a data tem de ser
   real, não pode ser do futuro nem ter mais de dois anos; e uma linha que diga
   "Data" ganha sempre a outra que não diga. */
const dataDoTalao = (texto) => {
    if (!texto) return '';
    const hoje = hojeISO();
    const limite = limiteAntigoISO();
    const padroes = [
        /\b(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})\b/,          // 2026-07-31
        /\b(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})\b/,          // 31-07-2026
        /\b(\d{1,2})[-/.](\d{1,2})[-/.](\d{2})\b/,          // 31-07-26
    ];
    const candidatas = [];
    texto.split('\n').forEach(linha => {
        const L = linha.toUpperCase();
        if (/VALID|VENCIMENTO|PRAZO|ENTREGA/.test(L)) return;   // não é a data do documento
        padroes.forEach((re, i) => {
            const m = linha.match(re);
            if (!m) return;
            let ano, mes, dia;
            if (i === 0) { [, ano, mes, dia] = m; }
            else { [, dia, mes, ano] = m; if (i === 2) ano = String(2000 + Number(ano)); }
            const iso = `${String(ano).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
            const d = new Date(`${iso}T00:00:00`);
            if (Number.isNaN(d.getTime())) return;
            if (d.getUTCDate && String(d.getDate()).padStart(2, '0') !== String(dia).padStart(2, '0')) return;
            if (iso > hoje || iso < limite) return;
            candidatas.push({ iso, comRotulo: /DATA|EMISS/.test(L) });
        });
    });
    if (!candidatas.length) return '';
    const rotulada = candidatas.find(c => c.comRotulo);
    return (rotulada || candidatas[0]).iso;
};

const quemAdquiriu = (nif) => {
    const n = String(nif || '').trim();
    if (!n) return '—';
    if (n === NIF_SPROD) return 'Em nome da SPROD';
    if (n === '999999990') return 'Consumidor final';
    return n;   // NIF de outra entidade: a fatura não é da empresa
};

/* ============================================================
   REGISTOS TARDIOS

   Inverte o eixo do tempo. A listagem normal responde a "que despesas são
   deste mês?"; aqui pergunta-se "o que foi CARREGADO depois de o mês da fatura
   já ter sido extraído e reconciliado?".

   Sem esta vista, uma fatura de junho carregada em agosto fica invisível: só
   apareceria na vista de junho, que já não se volta a abrir depois de fechado.

   O critério não é uma tolerância arbitrária em dias — é o processo real: o mês
   M é extraído por volta do dia 6 de M+1. Tudo o que entrar a partir desse dia,
   para uma fatura de M ou anterior, chegou depois da extração.
   ============================================================ */
/* A janela por omissão vem do contexto — é a mesma que alimenta o contador da
   barra lateral. Duplicá-la aqui era o que fazia os dois números discordarem. */

const RegistosTardios = () => {
    const { marcarTratado } = useTardios();
    const [aAtualizar, setAAtualizar] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [diaFecho, setDiaFecho] = useState(6);
    const [desde, setDesde] = useState(janelaPadraoDesde);
    const [linhas, setLinhas] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');

    useEffect(() => {
        let cancelado = false;
        setCarregando(true);
        setErro('');
        api.get('/despesas', { params: { tardios: 1, dia_fecho: diaFecho, desde, _t: Date.now() } })
            .then(r => { if (!cancelado) setLinhas(r.data || []); })
            .catch(e => { if (!cancelado) setErro(e.response?.data?.error || e.message); })
            .finally(() => { if (!cancelado) setCarregando(false); });
        return () => { cancelado = true; };
    }, [diaFecho, desde]);

    // Agrupado pelo mês da FATURA — é essa a unidade de trabalho: cada grupo
    // corresponde a um mês cuja documentação tem de ser refeita.
    const grupos = useMemo(() => {
        const m = new Map();
        linhas.forEach(l => {
            const chave = (l.data_documento || '').slice(0, 7);   // AAAA-MM
            if (!m.has(chave)) m.set(chave, []);
            m.get(chave).push(l);
        });
        return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]));
    }, [linhas]);

    const nomeMes = (aaaaMM) => {
        if (!aaaaMM) return '—';
        const [a, m] = aaaaMM.split('-');
        const nome = new Date(0, Number(m) - 1).toLocaleString('pt-PT', { month: 'long' });
        return `${nome.charAt(0).toUpperCase()}${nome.slice(1)}/${a}`;
    };
    const dataPt = (d) => (d ? new Date(d).toLocaleDateString('pt-PT') : '—');

    /* Manda o reconciliador voltar a cruzar TODOS os meses com as despesas já
       lançadas aqui. O cruzamento original corre uma vez, quando o extrato é
       carregado; tudo o que entre depois fica invisível do lado de lá. Só
       promove movimentos por justificar — nunca desfaz associações feitas à
       mão — e cada um fica com a data de hoje como lote, que passa a sair no
       documento ("Fatura inserida em …") e numa versão nova do histórico. */
    const atualizarReconciliacoes = async () => {
        setAAtualizar(true); setErro(''); setResultado(null);
        try {
            await api.post('/reconciliador/sessao');
            const r = await api.post('/reconciliador/recruzar-tudo');
            /* Os meses que estavam nesta lista ficam dados como revistos: as
               faturas já foram levar o visto ao extrato, não há mais nada a
               fazer com elas aqui. A marca guarda o instante, por isso um mês
               volta a aparecer se entrar outra despesa depois de agora. */
            const meses = [...new Set(linhas.map(l => (l.data_documento || '').slice(0, 7)).filter(Boolean))];
            await Promise.all(meses.map(m => {
                const [a, mm] = m.split('-');
                return marcarTratado(Number(a), Number(mm)).catch(() => { });
            }));
            setLinhas([]);
            setResultado(r.data);
        } catch (e) {
            setErro(e.response?.data?.erro || e.response?.data?.error || e.message);
        } finally { setAAtualizar(false); }
    };

    return (
        <div className="tardios-wrap">
            <div className="tardios-header">
                <div>
                    <h3><LiquidText>⏰ Registos tardios</LiquidText></h3>
                    <p>
                        Despesas de cartão carregadas <strong>depois</strong> de o mês da fatura
                        já ter sido extraído e reconciliado.
                    </p>
                </div>
                <div className="tardios-filtros">
                    <label>
                        Fecho no dia
                        <input type="number" min="1" max="28" value={diaFecho}
                            onChange={e => setDiaFecho(e.target.value)} />
                        do mês seguinte
                    </label>
                    <label>
                        Carregadas desde
                        <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
                    </label>
                </div>
            </div>

            {carregando && <p className="tardios-info">A procurar…</p>}
            {erro && <p className="tardios-erro">Erro ao consultar: {erro}</p>}
            {resultado && (
                <p className={`tardios-info ${resultado.promovidos ? 'tardios-ok' : ''}`}>
                    {resultado.promovidos
                        ? <><OrcamentoIcon name="check" fallback="✅" /> {resultado.promovidos} movimento(s) do extrato passaram a ter fatura,
                            em {resultado.meses.map(m => `${m.nome} (${m.promovidos})`).join(', ')}.
                            Ficaram marcados com a data de hoje, o documento de cada mês foi regenerado
                            com uma versão nova, e esta lista fica limpa.</>
                        : <>Nenhum movimento novo para associar — os {resultado.periodos_vistos} meses
                            com extrato já estavam em dia. A lista fica limpa; se entrar outra despesa
                            fora de prazo, volta a aparecer.</>}
                </p>
            )}
            {!carregando && !erro && grupos.length === 0 && (
                <p className="tardios-info tardios-ok">
                    <OrcamentoIcon name="check" fallback="✅" /> Nada carregado fora de prazo neste período.
                </p>
            )}

            {!carregando && !erro && grupos.map(([mes, itens]) => (
                <div key={mes} className="tardios-grupo">
                    <div className="tardios-grupo-titulo">
                        {nomeMes(mes)} — {itens.length} despesa(s) carregada(s) depois do fecho
                        <span className="tardios-grupo-total">
                            {fmtMoney(itens.reduce((s, l) => s + (parseFloat(l.valor_total) || 0), 0))}
                        </span>
                        {/* Um botão só: leva as faturas ao extrato do mês e do cartão
                            certos e dá a lista por tratada. Havia antes um "Já tratei"
                            que apenas calava o aviso sem tocar no extrato — deixava o
                            movimento a vermelho do lado do contabilista com a fatura já
                            cá dentro, que é o pior dos dois mundos. */}
                        <button className="tardios-btn-atualizar"
                            disabled={aAtualizar}
                            title="Associa, em todos os meses, os movimentos do extrato que já têm fatura lançada aqui. Marca cada um com a data de hoje e gera uma versão nova do documento."
                            onClick={atualizarReconciliacoes}>
                            <LiquidText>{aAtualizar ? 'A atualizar…' : '🔄 Associar e dar meses por revistos'}</LiquidText>
                        </button>
                    </div>
                    <table className="standard-table tardios-tabela">
                        <thead>
                            <tr>
                                <th>Data fatura</th>
                                <th>Fornecedor</th>
                                <th>Cartão</th>
                                <th style={{ textAlign: 'right' }}>Valor</th>
                                <th>Carregada em</th>
                                <th>Por</th>
                            </tr>
                        </thead>
                        <tbody>
                            {itens.map(l => (
                                <tr key={l.id}>
                                    <td>{dataPt(l.data_documento)}</td>
                                    <td>{l.fornecedor || l.nif_emissor || '—'}</td>
                                    <td>{l.cartao_nome || '—'}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMoney(l.valor_total)}</td>
                                    <td className="tardios-carga">{dataPt(l.criado_em)}</td>
                                    <td>{l.utilizador_nome || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
};

// Comprovativo carregado via endpoint autenticado (blob) — o acesso estático
// a /uploads/despesas e /uploads/faturas-fornecedor está bloqueado no servidor.
// Aceita imagem ou PDF: o PDF é pré-visualizado no próprio ecrã (ou como ícone,
// na miniatura da lista) em vez de aparecer como imagem partida.
// `versao` muda quando o ficheiro é substituído, para não vir do cache.
const ComprovativoImg = ({ despesaId, style, miniatura = false, versao }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [ePdf, setEPdf] = useState(false);
    useEffect(() => {
        let url = null;
        let cancelled = false;
        api.get(`/gestao-financeira/documento/${despesaId}`, { responseType: 'blob', params: versao ? { v: versao } : undefined })
            .then(r => {
                if (cancelled) return;
                const pdf = (r.data.type || '').includes('pdf');
                url = URL.createObjectURL(pdf ? new Blob([r.data], { type: 'application/pdf' }) : r.data);
                setEPdf(pdf);
                setBlobUrl(url);
            })
            .catch(() => { if (!cancelled) setBlobUrl(null); });
        return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    }, [despesaId, versao]);
    if (!blobUrl) return null;

    if (ePdf) {
        if (miniatura) {
            return (
                <a href={blobUrl} target="_blank" rel="noreferrer" title="Abrir PDF"
                    onClick={e => e.stopPropagation()}
                    style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, textDecoration: 'none', background: '#fef2f2' }}><OrcamentoIcon name="document" fallback="📄" /></a>
            );
        }
        return (
            <div>
                <object data={blobUrl} type="application/pdf" style={{ ...style, height: 420 }} aria-label="Comprovativo em PDF">
                    <p style={{ padding: 12, margin: 0, fontSize: 13, color: '#64748b' }}>Pré-visualização indisponível neste navegador.</p>
                </object>
                <a href={blobUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 6, fontSize: 12, color: '#2b6cb0' }}><OrcamentoIcon name="document" fallback="📄" /> Abrir PDF numa janela</a>
            </div>
        );
    }
    return <img src={blobUrl} alt="Fatura" style={style} />;
};

// Movimentos Revolut de 0,00 € são verificações de cartão (pré-autorizações do
// comerciante): não há fatura para juntar, por isso contam como tratados. Se a
// Revolut lhes vier a atribuir valor, voltam sozinhos aos que faltam justificar.
const ehVerificacaoCartao = (d) => d.origem === 'revolut' && parseFloat(d.valor_total || 0) === 0;

// Mesmo limite do CSS: no telemóvel/tablet a página abre no registo de faturas,
// seja qual for a conta usada — quem está no terreno vem para registar, não para
// consultar listas. Do registo há sempre "← Voltar" para a lista.
const ecraPequeno = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

/* ── Leitura do QR da AT ──
   O QR das faturas é denso (60 a 75 módulos) e o html5-qrcode descodifica a
   zona da caixa num canvas com o tamanho dela em píxeis CSS (300 px dava 2 a 4
   px por módulo, no limite). Por isso pede-se a câmara em alta resolução, o
   leitor é desenhado com QR_ESCALA_LEITOR vezes a largura e reduzido com
   transform (o canvas fica com esse múltiplo de píxeis) e aplica-se zoom onde
   o aparelho deixa, para o QR encher a caixa a uma distância a que a câmara
   foca. Sem ResizeObserver não há como acertar a altura: fica à escala 1. */
const QR_ESCALA_LEITOR = typeof ResizeObserver === 'undefined' ? 1 : 2;
const QR_SEGUNDOS_DICA = 20;      // conta só desde que a câmara mostra imagem
const QR_SEGUNDOS_DESLIGAR = 120; // não deixar a câmara ligada sem fim
const QR_VIDEO_HD = { width: { ideal: 1920 }, height: { ideal: 1080 } };
const QR_LEITOR_CONFIG = {
    verbose: false,
    formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
    useBarCodeDetectorIfSupported: true,
};
const ehIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// Traduz a falha ao abrir a câmara numa mensagem que diga o que fazer.
const mensagemErroCameraQr = (err) => {
    const t = `${(err && err.name) || ''} ${(err && err.message) || ''} ${String(err || '')}`;
    if (/NotAllowed|Permission|denied|SecurityError/i.test(t)) {
        return ehIOS()
            ? 'A permissão para usar a câmara foi recusada. Para a repor: toque em «aA» na barra do endereço › Definições do site › Câmara › Permitir (ou Ajustes › Safari › Câmara). Também pode fotografar o QR.'
            : 'A permissão para usar a câmara foi recusada. Para a repor: toque no cadeado (ou no ícone ao lado do endereço) › Permissões › Câmara › Permitir, e volte a tentar. Também pode fotografar o QR.';
    }
    if (/NotFound|DevicesNotFound|device not found|Overconstrained/i.test(t)) {
        return 'Não foi encontrada nenhuma câmara traseira disponível neste aparelho. Pode fotografar o QR ou fazer o registo manual.';
    }
    if (/NotReadable|TrackStart|Could not start|AbortError|in use/i.test(t)) {
        return 'A câmara está a ser usada por outra aplicação (por exemplo uma chamada ou outra app de câmara). Feche-a e volte a tentar.';
    }
    if (/mediaDevices|not supported/i.test(t)) {
        return 'Este browser não dá acesso à câmara. Use o Chrome ou o Safari atualizados, ou fotografe o QR.';
    }
    return `Não foi possível abrir a câmara (${String((err && err.message) || err || 'erro desconhecido').slice(0, 120)}).`;
};

// Fotografias de 12 MP demoram a descodificar: tenta-se primeiro uma cópia
// reduzida e só depois o original.
const reduzirImagemQr = (file, ladoMax) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        const escala = ladoMax / Math.max(img.naturalWidth, img.naturalHeight);
        if (!(escala < 1)) { URL.revokeObjectURL(url); resolve(null); return; }
        const c = document.createElement('canvas');
        c.width = Math.round(img.naturalWidth * escala);
        c.height = Math.round(img.naturalHeight * escala);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        URL.revokeObjectURL(url);
        c.toBlob((b) => resolve(b ? new File([b], 'qr.jpg', { type: 'image/jpeg' }) : null), 'image/jpeg', 0.92);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
});

// Lê o QR de uma fotografia em resolução total (o elemento #reader-foto está
// sempre na página, escondido).
const descodificarFotoQr = async (file) => {
    const leitor = new Html5Qrcode('reader-foto', QR_LEITOR_CONFIG);
    const tentativas = [];
    const reduzida = await reduzirImagemQr(file, 2000);
    if (reduzida) tentativas.push(reduzida);
    tentativas.push(file);
    let ultimoErro = null;
    try {
        for (const f of tentativas) {
            try { return await leitor.scanFile(f, false); } catch (err) { ultimoErro = err; }
        }
    } finally {
        try { leitor.clear(); } catch (e) { /* nada a limpar */ }
    }
    throw ultimoErro || new Error('QR não encontrado');
};

/* Filtro de coluna da lista de despesas.

   A lista já vem toda para o browser, por isso o filtro trabalha aqui e é
   imediato — ao contrário do das Encomendas a Fornecedor, que tem 7600 linhas
   paginadas e precisa de ir ao servidor.

   Escolha múltipla: conjunto vazio quer dizer "todos", que é o estado normal. */
const FiltroColuna = ({ titulo, valores, escolhidos, aoMudar }) => {
    const [aberto, setAberto] = useState(false);
    const [busca, setBusca] = useState('');
    const caixaRef = useRef(null);

    useEffect(() => {
        if (!aberto) return;
        const fora = (e) => {
            if (caixaRef.current && !caixaRef.current.contains(e.target)) {
                setAberto(false); setBusca('');
            }
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [aberto]);

    const visiveis = busca
        ? valores.filter(v => v.toLowerCase().includes(busca.toLowerCase()))
        : valores;

    const alternar = (v) => {
        const novo = new Set(escolhidos);
        if (novo.has(v)) novo.delete(v); else novo.add(v);
        aoMudar(novo);
    };

    return (
        <span className="filtro-col" ref={caixaRef}>
            <button type="button"
                className={`filtro-col-btn ${escolhidos.size ? 'ativo' : ''}`}
                onClick={(e) => { e.stopPropagation(); setAberto(a => !a); }}
                title={escolhidos.size
                    ? `${titulo}: ${[...escolhidos].join(', ')}`
                    : `Filtrar por ${titulo.toLowerCase()}`}
                aria-label={`Filtrar por ${titulo.toLowerCase()}`}>
                ▼{escolhidos.size > 0 && <span className="filtro-col-conta">{escolhidos.size}</span>}
            </button>
            {aberto && (
                <div className="filtro-col-painel" onClick={e => e.stopPropagation()}>
                    {valores.length > 8 && (
                        <input className="filtro-col-busca" type="text" autoFocus
                            placeholder="Procurar..." value={busca}
                            onChange={e => setBusca(e.target.value)} />
                    )}
                    <div className="filtro-col-acoes">
                        <button type="button" onClick={() => aoMudar(new Set(visiveis))}>
                            Todos{busca ? ' os encontrados' : ''}
                        </button>
                        <button type="button" onClick={() => aoMudar(new Set())}>Limpar</button>
                    </div>
                    <div className="filtro-col-lista">
                        {visiveis.length === 0 && <p className="filtro-col-vazio">Nada encontrado.</p>}
                        {visiveis.map(v => (
                            <label key={v} className="filtro-col-item">
                                <input type="checkbox" checked={escolhidos.has(v)} onChange={() => alternar(v)} />
                                <span>{v}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </span>
    );
};

/* Cabeçalho de coluna: o título ordena, o funil filtra. São duas ações
   distintas no mesmo sítio, por isso o funil pára o clique antes de ele chegar
   ao título — senão filtrar ordenava também. */
const CabecalhoCol = ({ campo, titulo, ordem, aoOrdenar, filtro, ...resto }) => {
    const ativo = ordem.col === campo;
    return (
        <th {...resto}>
            <span className="th-ordenavel" onClick={() => aoOrdenar(campo)}
                title={`Ordenar por ${titulo.toLowerCase()}`}>
                <LiquidText>{titulo}</LiquidText>
                <span className={`th-seta ${ativo ? 'ativa' : ''}`}>
                    {ativo ? (ordem.dir === 'asc' ? '▲' : '▼') : '⇅'}
                </span>
            </span>
            {filtro}
        </th>
    );
};

/* Como se lê cada coluna filtrável. Uma função por coluna, para o filtro e a
   lista de valores possíveis nunca poderem discordar. */
const ORDEM_COLUNAS = {
    estado: (d) => (ehVerificacaoCartao(d) ? 2 : d.origem === 'revolut' ? 1 : 0),
    data: (d) => String(d.data_documento || d.criado_em || ''),
    fornecedor: (d) => (d.fornecedor || d.nif_emissor || '').toLowerCase(),
    numero: (d) => String(d.numero_documento_completo || '').toLowerCase(),
    cartao: (d) => (d.cartao_nome || '').toLowerCase(),
    login: (d) => (d.utilizador_nome || '').toLowerCase(),
    total: (d) => parseFloat(d.valor_total || 0),
};

/* A chave que casa um movimento desta lista com o mesmo movimento no
   reconciliador. Não há id comum: o extrato da Revolut não guarda o id da
   transação, por isso usa-se (4 dígitos do cartão, dia, valor). Emparelhou os
   160 movimentos de julho de 2026 sem sobrar nenhum. */
const chaveRevolut = (d) => {
    const dt = String(d.data_documento || '').slice(0, 10);
    if (!d.card_last4 || dt.length < 10) return null;
    return `${d.card_last4}|${dt.slice(8, 10)}.${dt.slice(5, 7)}|${parseFloat(d.valor_total || 0).toFixed(2)}`;
};

// As caixas de escolha: um pouco maiores do que o tamanho de origem, que
// numa lista densa é pequeno demais para acertar à primeira.
const CAIXA = { width: 15, height: 15, cursor: 'pointer', margin: 0 };
// O mesmo invólucro nas três células: uma caixa solta assenta na linha de
// base do texto e fica desalinhada da vizinha que vive dentro de um flex.
const CELULA_CAIXA = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, minHeight: 15 };

const COLUNAS_FILTRAVEIS = {
    /* "Com fatura" juntava numa só entrada o que está fechado e o que ainda dá
       trabalho: a despesa lançada sem imagem nenhuma. Era o único conjunto
       acionável que o funil não conseguia isolar. */
    estado: (d) => (ehVerificacaoCartao(d) ? 'Verificação de cartão'
                  : d.origem !== 'revolut' ? (d.caminho_foto ? 'Com fatura e imagem' : 'Com fatura, sem imagem')
                  : d.justificada_recon ? 'Já justificada no reconciliador'
                  : 'Sem fatura'),
    data: (d) => (d.data_documento ? new Date(d.data_documento).toLocaleDateString() : '—'),
    fornecedor: (d) => d.fornecedor || d.nif_emissor || '—',
    numero: (d) => String(d.numero_documento_completo || '').trim() || '—',
    cartao: (d) => d.cartao_nome || '—',
    // Quem tinha sessão iniciada ao registar. Filtrar por aqui é o que permite
    // ver de uma vez tudo o que uma pessoa lançou, para lhe escrever uma vez só.
    login: (d) => d.utilizador_nome || '—',
    // «M»: despesa carregada como manutenção (o projeto da manutenção do
    // titular do cartão ou de quem a registou — o servidor diz qual é).
    manutencao: (d) => (d.manutencao ? 'Manutenção' : 'Sem manutenção'),
};

/* «📁 Projeto / Obra» ou «🔧 Manutenção» — nos cartões com o visto
   «Manutenção» (Trabalhadores → Cartões Associados). A manutenção vê-se pela
   palavra: o número do orçamento fica do lado do servidor. */
const EscolhaManutencao = ({ manutencao, aoEscolher, compacto = false }) => (
    <div style={{ display: 'flex', gap: 8, marginBottom: manutencao ? 0 : 8 }}>
        {[[false, '📁 Projeto / Obra'], [true, '🔧 Manutenção']].map(([valor, rotulo]) => (
            <button key={rotulo} type="button" onClick={() => aoEscolher(valor)}
                style={{ flex: 1, padding: compacto ? '7px' : '12px', borderRadius: 10, cursor: 'pointer',
                    fontSize: compacto ? '0.85rem' : '1rem', fontWeight: 600,
                    border: manutencao === valor ? '2px solid #3182ce' : '1px solid #e2e8f0',
                    background: manutencao === valor ? '#ebf8ff' : 'white',
                    color: manutencao === valor ? '#2b6cb0' : '#4a5568' }}>
                {rotulo}
            </button>
        ))}
    </div>
);

const Despesas = () => {
    const liquidFeedback = useLiquidFeedback();
    const { user } = useAuth();
    const { meses: tardiosMeses, totalMeses: tardiosTotalMeses } = useTardios();
    const navigate = useNavigate();
    const [despesas, setDespesas] = useState([]);
    /* Estado das suspeitas de abastecimento, por despesa. Vem à parte da lista
       porque é calculado a cada pedido (as regras mudam quando se marca um NIF)
       e porque falhar aqui não pode partir a lista de despesas. */
    const [suspeitas, setSuspeitas] = useState({});
    // Despesas escolhidas para aviso, pela caixa antes do fornecedor.
    const [escolhidas, setEscolhidas] = useState(() => new Set());
    /* Movimentos sem fatura escolhidos para se pedir o talão. Coluna própria,
       antes do Estado: é outro assunto e outro destinatário — aqui não há
       despesa nem login, pede-se ao titular do cartão. */
    const [escolhidosMov, setEscolhidosMov] = useState(() => new Set());
    const [movAvisados, setMovAvisados] = useState({});
    const alternarMov = (id) => setEscolhidosMov(p => { const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s; });
    const alternarEscolha = (id) => setEscolhidas(p => { const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s; });
    const [cartoes, setCartoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showScanner, setShowScanner] = useState(false);
    const [showCardManager, setShowCardManager] = useState(false);
    const [isScanning, setIsScanning] = useState(false);
    const [manualEntry, setManualEntry] = useState(false);
    const [valorTotal, setValorTotal] = useState('');
    /* A data da fatura no registo manual. Sem QR não há nada que a diga. Começa
       vazia e é obrigatória: vir preenchida com o dia de hoje era um convite a
       deixá-la ficar, e punha os talões de outros dias no dia (e no mês) errado. */
    const [dataManual, setDataManual] = useState('');
    /* O que o talão diz, quando se consegue ler. Nunca preenche o campo
       sozinho: aparece como sugestão, e serve para confrontar quem escreveu
       outra coisa. */
    const [dataOcr, setDataOcr] = useState('');
    /* Nada se grava sem uma confirmação explícita da data. Qualquer alteração
       à data — ou uma fotografia nova — obriga a confirmar outra vez. */
    const [dataConfirmada, setDataConfirmada] = useState(false);
    const [pedirDataAgora, setPedirDataAgora] = useState(false);
    // Cada registo manual abre com a data vazia: nunca herda a do anterior.
    useEffect(() => {
        if (manualEntry) { setDataManual(''); setDataConfirmada(false); }
    }, [manualEntry]);
    /* O que o banco diz sobre esta data. Só existe para cartões que recebem
       movimentos pela API (Revolut): num BCP não há com que comparar até o
       extrato chegar. */
    const [confereBanco, setConfereBanco] = useState(null);

    const [selectedCartao, setSelectedCartao] = useState('');
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [photoPdfNome, setPhotoPdfNome] = useState(null); // comprovativo em PDF (não tem pré-visualização de imagem)
    const [novoComprovativo, setNovoComprovativo] = useState(null); // ficheiro escolhido para substituir o anexo
    const [submitting, setSubmitting] = useState(false);
    const envioDespesaRef = useRef(false);
    const [resultadoRegisto, setResultadoRegisto] = useState(null);
    const [selectedDespesa, setSelectedDespesa] = useState(null);
    // No telemóvel entra-se logo no registo pela câmara — a consulta de faturas é
    // trabalho de secretária. Contas de escritório têm "← Voltar" para a lista.
    const [captureMode, setCaptureMode] = useState(user?.perfil_mobile || ecraPequeno());
    // painel ativo na página: lista de despesas ou reconciliação de extratos
    const [painel, setPainel] = useState('despesas'); // despesas | extratos
    // Muda a cada clique num separador, mesmo repetindo o que já está ativo.
    // É o que permite voltar ao início da secção depois de entrar num cartão.
    const [painelClique, setPainelClique] = useState(0);
    const isMobileUser = user?.perfil_mobile;
    const [scanResult, setScanResult] = useState(null);
    const [idAlocacao, setIdAlocacao] = useState('');
    /* Opção «Manutenção»: { projeto_empresa, cartoes, ultimo_projeto }
       (cartoes[id] = { manutencao, ultimo_projeto }; ultimo_projeto = último
       número usado no cartão numa compra que não foi da manutenção).
       paraManutencao é a escolha da compra que se está a registar. */
    const [opcoesRegisto, setOpcoesRegisto] = useState(null);
    const [paraManutencao, setParaManutencao] = useState(false);
    // Despesa de combustível: viatura, kms do conta-quilómetros e litros
    const [tipoDespesa, setTipoDespesa] = useState('');   // '' (geral) | 'combustivel'
    const [veiculoId, setVeiculoId] = useState('');
    const [kmsAbastecimento, setKmsAbastecimento] = useState('');
    const [litros, setLitros] = useState('');
    const [veiculos, setVeiculos] = useState([]);
    /* Viatura alugada acrescentada aqui mesmo.

       Um aluguer aparece a meio da semana e o abastecimento é feito no dia. Se
       a viatura só pudesse nascer no ecrã de Veículos — que quem está na rua
       não tem —, o registo ficava por fazer ou era lançado como despesa geral,
       que é precisamente o que andamos a tentar evitar. */
    const [novaViatura, setNovaViatura] = useState(null);
    const [aGuardarViatura, setAGuardarViatura] = useState(false);
    const registoDespesaRef = useRef(null);

    /* A matrícula vai-se formatando à medida que se escreve: quem está na bomba
       não tem de se lembrar dos traços, e sai sempre na mesma forma — é por ela
       que os abastecimentos da mesma viatura se juntam. */
    const formatarMatricula = (t) => {
        const so = String(t).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
        return so.match(/.{1,2}/g)?.join('-') || '';
    };

    const guardarViatura = async () => {
        const matricula = formatarMatricula(novaViatura?.matricula || '');
        if (matricula.length !== 8) {
            setNovaViatura(v => ({ ...v, erro: 'A matrícula tem de ter seis caracteres, na forma AB-12-CD.' }));
            return;
        }
        setAGuardarViatura(true);
        try {
            const r = await api.post('/veiculos', { matricula, alugada: true });
            setVeiculos(l => l.some(x => x.id === r.data.id) ? l : [...l, r.data]);
            setVeiculoId(String(r.data.id));
            setNovaViatura(null);
            return String(r.data.id);
        } catch (e) {
            setNovaViatura(v => ({ ...v, erro: e.response?.data?.error || e.message }));
        } finally { setAGuardarViatura(false); }
    };

    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [detectedAtcud, setDetectedAtcud] = useState('');
    const [detectedFornecedor, setDetectedFornecedor] = useState(null);
    const [qrNif, setQrNif] = useState('');
    const [qrValor, setQrValor] = useState('');
    /* O cartão com que o banco diz que se pagou, quando não é o escolhido.

       Quem tem dois ou três cartões fica com o que vem pré-preenchido — o
       habitual —, e muitas vezes não foi esse que pagou. A despesa ficava
       lançada no cartão errado, o movimento do banco não a encontrava, e a
       mesma compra aparecia duas vezes. Ao ler o QR procura-se o pagamento nos
       movimentos Revolut de quem está a lançar; se estiver noutro cartão,
       pergunta-se. Só se pergunta: escolher continua a ser de quem lança.

       Limite: o movimento só está cá depois de a sincronização o trazer (de
       4 em 4 horas). Lançado ao balcão, no minuto da compra, ainda não há nada
       para propor — e aí é o emparelhamento do servidor que corrige depois. */
    const [cartaoProvavel, setCartaoProvavel] = useState(null);
    const procurarCartaoProvavel = async (valor, data) => {
        setCartaoProvavel(null);
        if (!valor) return;
        try {
            const r = await api.get('/despesas/cartao-provavel', { params: { valor, data: data || undefined } });
            setCartaoProvavel(r.data && r.data.cartao_id ? r.data : null);
        } catch {
            // sem sugestão não é erro: fica o cartão escolhido
        }
    };
    const [showCropper, setShowCropper] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const [qrErrorMsg, setQrErrorMsg] = useState('');
    const [nifManual, setNifManual] = useState('');
    const [paisNifManual,setPaisNifManual]=useState('PT');
    const [numeroManual, setNumeroManual] = useState('');
    const [detailEditing, setDetailEditing] = useState(false);
    /* PROJETO e EF no detalhe dos cartões com pede_ef: escrevem-se logo, sem
       «Editar», e gravam-se ao sair do campo (ou com Enter). */
    const [efProjeto, setEfProjeto] = useState({ projeto: '', ef: '', estado: '', erro: '' });
    const despesaAbertaRef = useRef(null);
    useEffect(() => {
        despesaAbertaRef.current = selectedDespesa?.id ?? null;
        setEfProjeto({ projeto: selectedDespesa?.id_alocacao || '', ef: selectedDespesa?.ef_numero || '', estado: '', erro: '' });
    }, [selectedDespesa?.id]);
    const cropperInstanceRef = useRef(null);
    const cropImageRef = useRef(null);

    // Filter states
    /* O gasto do mês vem do reconciliador, que é quem tem os extratos. Não é
       recalculado aqui de propósito: a pergunta "quanto se gastou" tem de ter
       uma resposta só, vinda de um sítio só. */
    const [gastoCartoes, setGastoCartoes] = useState(null);
    /* O que se vai exportar, enquanto a janela está aberta: {tipo, mes, ano,
       cartao_id}. Nasce com o que está escolhido na caixa de filtros. */
    const [exportar, setExportar] = useState(null);
    /* O mês corrente ainda não tem extratos, mas os movimentos Revolut chegam
       pela API à medida que acontecem. É a única parte do mês que já se sabe. */
    const [revolutMes, setRevolutMes] = useState(null);
    /* Movimentos que o reconciliador já dá por justificados. A SPROD Gestão só
       sabe de despesas lançadas aqui; a fatura pode ter sido anexada lá, e
       nesse caso não há nada a pedir a ninguém. */
    const [justificadas, setJustificadas] = useState(() => new Set());

    /* Filtros de coluna. Conjunto vazio por coluna = sem filtro. */
    const [colFiltros, setColFiltros] = useState({});
    /* Ordenação: primeiro clique ascendente, segundo descendente, terceiro
       volta à ordem natural da lista (a que vem do servidor). */
    const [ordem, setOrdem] = useState({ col: null, dir: 'asc' });
    const ordenarPor = (campo) => setOrdem(o =>
        o.col !== campo ? { col: campo, dir: 'asc' }
        : o.dir === 'asc' ? { col: campo, dir: 'desc' }
        : { col: null, dir: 'asc' });
    const porColuna = (campo, novo) => setColFiltros(f => ({ ...f, [campo]: novo }));
    const escolhidos = (campo) => colFiltros[campo] || new Set();
    const haFiltro = Object.values(colFiltros).some(c => c && c.size > 0);
    const limparColunas = () => setColFiltros({});
    const [filterMes, setFilterMes] = useState(new Date().getMonth() + 1);
    const [filterAno, setFilterAno] = useState(new Date().getFullYear());
    /* Não há filtro de Empresa: as 3288 despesas e os 25 cartões ativos são
       todos "Sprod", e escolher "Besmart" esvaziava o ecrã em qualquer mês. O
       servidor continua a aceitar o parâmetro — voltar a pôr a caixa são seis
       linhas. */
    const [filterCartao, setFilterCartao] = useState('');
    const [filterFornecedor, setFilterFornecedor] = useState('');
    const [filterValorMin, setFilterValorMin] = useState('');
    const [filterValorMax, setFilterValorMax] = useState('');

    const scannerRef = useRef(null);

    // Parse ATCUD/QR string client-side (same logic as backend)
    const parseAtcudClient = (input) => {
        if (!input) return {};
        const data = {};
        const pairs = input.split('*');
        pairs.forEach(pair => {
            const [key, ...valueParts] = pair.split(':');
            if (key && valueParts.length > 0) {
                data[key] = valueParts.join(':');
            }
        });
        return data;
    };

    useEffect(() => {
        fetchData();
        carregarOpcoesRegisto();
        loadOcrScript();
        loadCropperScript();
    }, [filterMes, filterAno, filterCartao, filterFornecedor, filterValorMin, filterValorMax, captureMode]);

    // Trocar de mês traz outros fornecedores e outras datas: manter os filtros
    // antigos escondia a lista inteira sem se perceber porquê.
    useEffect(() => { setColFiltros({}); }, [filterMes, filterAno, filterCartao, filterFornecedor]);

    /* O leitor de QR (html5-qrcode) vem no bundle, importado do npm: carregado
       do unpkg chegava tarde em rede fraca e o toque em «Ler QR Code» rebentava
       sem mensagem nenhuma. */

    const loadOcrScript = () => {
        if (!window.Tesseract) {
            const script = document.createElement('script');
            script.src = "https://unpkg.com/tesseract.js@v5.0.0/dist/tesseract.min.js";
            script.async = true;
            script.onload = () => console.log('Tesseract script loaded');
            document.body.appendChild(script);
        }
    };

    const loadCropperScript = () => {
        if (!window.Cropper) {
            const css = document.createElement('link');
            css.rel = 'stylesheet';
            css.href = 'https://cdnjs.cloudflare.com/ajax/libs/cropperjs/1.6.2/cropper.min.css';
            css.onload = () => {
                // Inject override styles AFTER cropper CSS loads
                const overrideStyle = document.createElement('style');
                overrideStyle.textContent = `
                    .cropper-crop-box .cropper-point { opacity:1!important; background-color:#3182ce!important; }
                    .cropper-crop-box .cropper-point.point-e,
                    .cropper-crop-box .cropper-point.point-w { width:18px!important; height:36px!important; border-radius:9px!important; }
                    .cropper-crop-box .cropper-point.point-n,
                    .cropper-crop-box .cropper-point.point-s { width:36px!important; height:18px!important; border-radius:9px!important; }
                    .cropper-crop-box .cropper-point.point-ne,
                    .cropper-crop-box .cropper-point.point-nw,
                    .cropper-crop-box .cropper-point.point-se,
                    .cropper-crop-box .cropper-point.point-sw { width:28px!important; height:28px!important; border-radius:50%!important; }
                `;
                document.head.appendChild(overrideStyle);
            };
            document.head.appendChild(css);

            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/cropperjs/1.6.2/cropper.min.js';
            script.async = true;
            script.onload = () => console.log('Cropper.js loaded');
            document.body.appendChild(script);
        }
    };

    /* ── Revolut: sincronizar, anexar fatura ao movimento, ignorar ── */
    const revFileRef = useRef(null);
    const [revTxAlvo, setRevTxAlvo] = useState(null);
    const [revBusy, setRevBusy] = useState(false);
    const [revFatura, setRevFatura] = useState(null);
    const [revErro, setRevErro] = useState('');
    const [zipBusy, setZipBusy] = useState(false);

    const sincronizarRevolut = async () => {
        setRevBusy(true);
        try {
            const r = await api.post('/revolut/sincronizar', { dias: 60 }, { timeout: 180000 });
            liquidFeedback.notify(`Revolut sincronizada:\n• ${r.data.novas} novas\n• ${r.data.conciliadas} conciliadas automaticamente`);
            fetchData();
        } catch (e) {
            liquidFeedback.notify('Erro ao sincronizar: ' + (e.response?.data?.error || e.message));
        } finally { setRevBusy(false); }
    };

    const anexarFaturaRevolut = (transacaoId) => {
        setRevTxAlvo(transacaoId);
        setTimeout(() => revFileRef.current?.click(), 0);
    };

    const enviarFaturaRevolut = (e) => {
        const ficheiro = e.target.files?.[0];
        e.target.value = '';
        if (!ficheiro || !revTxAlvo) return;
        setRevErro('');
        setRevFatura({ficheiro,nif:'',pais:'PT',numero:'',data:''});
    };
    const guardarFaturaRevolut = async (e) => {
        e.preventDefault();
        if (!revFatura || revBusy) return;
        setRevBusy(true); setRevErro('');
        try {
            const fd = new FormData();
            fd.append('foto',revFatura.ficheiro);
            fd.append('nif_emissor',revFatura.nif);
            fd.append('pais_nif',revFatura.pais);
            fd.append('numero_documento_completo',revFatura.numero);
            fd.append('data_documento',revFatura.data);
            await api.post(`/revolut/${revTxAlvo}/fatura`,fd,{timeout:120000});
            setRevFatura(null); setRevTxAlvo(null); fetchData();
        } catch(err) {
            setRevErro(err.response?.data?.error || 'Não foi possível anexar a fatura. Tente novamente.');
        } finally {setRevBusy(false);}
    };

    const ignorarRevolut = async (transacaoId) => {
        if (!await liquidFeedback.confirm('Marcar este movimento como "não gera fatura" (transferência, taxa, reembolso)?')) return;
        try {
            await api.post('/revolut/ignorar', { transacao_id: transacaoId });
            fetchData();
        } catch (e) { liquidFeedback.notify('Erro: ' + (e.response?.data?.error || e.message)); }
    };

    /* Os valores que cada coluna oferece saem da lista carregada, não de uma
       tabela fixa: mostra-se o que existe neste mês, e nada mais. */
    /* Cada movimento leva consigo se já está justificado do outro lado. Fica
       anotado uma vez, aqui, para o filtro, a lista e a grelha lerem todos o
       mesmo — em vez de cada um repetir a conta. */
    const despesasAnotadas = useMemo(() => despesas.map(d => (
        d.origem === 'revolut'
            ? { ...d, justificada_recon: justificadas.has(chaveRevolut(d) || '') }
            : d
    )), [despesas, justificadas]);

    const valoresDe = (campo) => {
        const f = COLUNAS_FILTRAVEIS[campo];
        return [...new Set(despesasAnotadas.map(f))].sort((a, b) =>
            campo === 'data'
                ? a.split('/').reverse().join('').localeCompare(b.split('/').reverse().join(''))
                : a.localeCompare(b, 'pt'));
    };

    const despesasFiltradas = useMemo(() => {
        const filtradas = despesasAnotadas.filter(d =>
            Object.entries(colFiltros).every(([campo, esc]) =>
                !esc || esc.size === 0 || esc.has(COLUNAS_FILTRAVEIS[campo](d))));
        if (!ordem.col) return filtradas;
        const chave = ORDEM_COLUNAS[ordem.col];
        const sinal = ordem.dir === 'asc' ? 1 : -1;
        // Cópia: ordenar no sítio mexia no array do estado.
        return [...filtradas].sort((a, b) => {
            const x = chave(a), y = chave(b);
            if (typeof x === 'number' && typeof y === 'number') return (x - y) * sinal;
            return String(x).localeCompare(String(y), 'pt') * sinal;
        });
    }, [despesasAnotadas, colFiltros, ordem]);

    /* Escolher um dia inteiro em vez de movimento a movimento. Guarda-se, por
       dia, os movimentos que ainda estão por justificar, e marca-se a linha em
       que a caixa do dia aparece — a primeira desse dia na ordem que está no
       ecrã. Ordenar por outra coluna espalha os dias pela lista; a caixa passa
       a aparecer na primeira linha de cada dia à mesma, só que fora de ordem. */
    const diasMov = useMemo(() => {
        const porDia = new Map();
        const primeira = new Map();
        for (const d of despesasFiltradas) {
            const podePedir = d.origem === 'revolut' && !ehVerificacaoCartao(d)
                && !d.justificada_recon && d.transacao_id;
            if (!podePedir) continue;
            const dia = String(d.data_documento || '').slice(0, 10);
            if (!dia) continue;
            if (!porDia.has(dia)) { porDia.set(dia, []); primeira.set(d.transacao_id, dia); }
            porDia.get(dia).push(d.transacao_id);
        }
        return { porDia, primeira };
    }, [despesasFiltradas]);

    /* Tudo escolhido nesse dia? tira. Caso contrário, junta o que falta —
       assim uma segunda vaga de movimentos do mesmo dia entra com um clique. */
    const alternarDia = (dia) => setEscolhidosMov(p => {
        const ids = diasMov.porDia.get(dia) || [];
        const s = new Set(p);
        if (ids.every(i => s.has(i))) ids.forEach(i => s.delete(i));
        else ids.forEach(i => s.add(i));
        return s;
    });

    const carregarOpcoesRegisto = () => api.get('/despesas/opcoes-registo', { params: { _t: Date.now() } })
        .then(r => setOpcoesRegisto(r.data))
        .catch(() => setOpcoesRegisto(null));

    const fetchData = async () => {
        try {
            const params = captureMode ? { recentes: true } : {
                mes: filterMes,
                ano: filterAno,
                cartao_id: filterCartao,
                fornecedor: filterFornecedor,
                valor_min: filterValorMin,
                valor_max: filterValorMax,
                incluir_revolut: 1, // lista única: faturas + movimentos Revolut sem fatura
                _t: Date.now(),
            };
            const [despesasRes, cartoesRes, veiculosRes] = await Promise.all([
                api.get('/despesas', { params }),
                api.get('/despesas/cartoes'),
                // quem não tem o módulo Veículos recebe 403: fica sem a lista, não sem o ecrã
                api.get('/veiculos').catch(() => ({ data: [] })),
            ]);
            setDespesas(despesasRes.data);
            setCartoes(cartoesRes.data);
            setVeiculos(Array.isArray(veiculosRes.data) ? veiculosRes.data : []);
            // Falhar aqui não pode partir o ecrã das despesas: se o
            // reconciliador não responder, fica sem o número em vez de sem lista.
            api.get('/reconciliador/api/gasto-cartoes', { params: { ano: filterAno, mes: filterMes } })
                .then(r => setGastoCartoes(r.data))
                .catch(() => setGastoCartoes(null));
            api.get('/combustivel/sem-fatura', { params: { _t: Date.now() } })
                .then(r => setMovAvisados(Object.fromEntries((r.data || []).map(x => [x.transacao_id, x]))))
                .catch(() => setMovAvisados({}));
            api.get('/combustivel/suspeitas', { params: { estado: 'todas', _t: Date.now() } })
                .then(r => setSuspeitas(Object.fromEntries((r.data || []).map(x => [x.despesa_id, x]))))
                .catch(() => setSuspeitas({}));
            api.get('/despesas/revolut-mes', { params: { ano: filterAno, mes: filterMes } })
                .then(r => setRevolutMes(r.data))
                .catch(() => setRevolutMes(null));
            api.get('/reconciliador/api/movimentos-justificados', { params: { ano: filterAno, mes: filterMes } })
                .then(r => setJustificadas(new Set(r.data?.chaves || [])))
                .catch(() => setJustificadas(new Set()));
            if (cartoesRes.data.length > 0 && !selectedCartao) {
                setSelectedCartao(cartoesRes.data[0].id);
            }
            return true;
        } catch (err) {
            console.error('Erro ao carregar dados:', err);
            return false;
        } finally {
            setLoading(false);
        }
    };

    /* O Excel e o ZIP levam o MÊS INTEIRO do cartão escolhido em cima. Os
       filtros da lista — fornecedor e funis das colunas — não viajam, e é de
       propósito: o ZIP é o que vai para o contabilista e é o que o reconciliador
       lê, por isso um mês arquivado com um filtro esquecido ficaria com metade
       dos talões sem ninguém dar por isso. O que faltava era dizê-lo no botão. */
    const alcanceExport = () => {
        const mesNome = new Date(0, Number(filterMes) - 1).toLocaleString('pt-PT', { month: 'long' });
        const cartao = cartoes.find(c => String(c.id) === String(filterCartao));
        return `Mês inteiro: todas as despesas de ${mesNome} de ${filterAno}`
            + (cartao ? `, do cartão ${cartao.nome}` : ', de todos os cartões')
            + '. Os filtros da lista (fornecedor e funis das colunas) não se aplicam.';
    };

    const handleExportExcel = async (escolha) => {
        const { mes, ano, cartao_id } = escolha || { mes: filterMes, ano: filterAno, cartao_id: filterCartao };
        try {
            const response = await api.get('/despesas/export', {
                params: {
                    mes,
                    ano,
                    cartao_id,
                    valor_min: filterValorMin,
                    valor_max: filterValorMax
                },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `despesas_${mes}_${ano}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error('Erro ao descarregar Excel:', err);
            liquidFeedback.notify('Erro ao gerar ficheiro Excel.');
        }
    };

    const handleExportZip = async (escolha) => {
        if (zipBusy) return;
        const { mes, ano, cartao_id, cartao_ids } = escolha || { mes: filterMes, ano: filterAno, cartao_id: filterCartao };
        setZipBusy(true);
        try {
            const response = await api.get('/despesas/export-zip', {
                params: {
                    mes,
                    ano,
                    cartao_id: cartao_ids ? undefined : cartao_id,
                    cartao_ids: cartao_ids?.join(','),
                    valor_min: filterValorMin,
                    valor_max: filterValorMax
                },
                responseType: 'blob',
                // Sem limite de tempo: um mês cheio são centenas de fotografias e
                // mais de 100 MB. O timeout global de 15 s do cliente serve para
                // pedidos normais e aqui abortava sempre a meio do download.
                timeout: 0
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `despesas_${mes}_${ano}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error('Erro ao descarregar ZIP:', err);
            liquidFeedback.notify('Erro ao gerar ficheiro ZIP.\n\n' + (err.message || ''));
        } finally {
            setZipBusy(false);
        }
    };

    const scannerTimeoutRef = useRef(null);   // dica ao fim de QR_SEGUNDOS_DICA
    const qrDesligarRef = useRef(null);       // desliga a câmara ao fim de QR_SEGUNDOS_DESLIGAR
    /* Cada arranque tem um número. Respostas que cheguem de um arranque antigo
       (a pessoa voltou atrás enquanto o browser ainda pedia a permissão, por
       exemplo) são ignoradas e essa câmara é desligada. */
    const qrSessaoRef = useRef(0);
    const qrFotoInputRef = useRef(null);
    const qrResizeRef = useRef(null);
    const [qrDica, setQrDica] = useState(false);
    const [qrZoom, setQrZoom] = useState(null);       // { niveis: [1, 2, 3], atual: 2, hd, focoContinuo }
    const [qrModoFoto, setQrModoFoto] = useState(false);
    const [qrLendoFoto, setQrLendoFoto] = useState(false);

    // A altura da janela do leitor acompanha o vídeo, que é desenhado ao
    // QR_ESCALA_LEITOR e reduzido com transform (que não ocupa espaço).
    const ligarReaderQr = useCallback((el) => {
        if (qrResizeRef.current) { qrResizeRef.current.disconnect(); qrResizeRef.current = null; }
        if (!el || QR_ESCALA_LEITOR === 1) return;
        const janela = el.parentElement && el.parentElement.parentElement;
        if (!janela) return;
        const ro = new ResizeObserver(() => {
            janela.style.height = `${Math.max(Math.round(el.offsetHeight / QR_ESCALA_LEITOR), 180)}px`;
        });
        ro.observe(el);
        qrResizeRef.current = ro;
    }, []);

    const limparTemporizadoresQr = () => {
        clearTimeout(scannerTimeoutRef.current);
        clearTimeout(qrDesligarRef.current);
    };

    // Desliga a câmara (se estiver ligada) e invalida o arranque em curso.
    const pararCameraQr = () => {
        limparTemporizadoresQr();
        qrSessaoRef.current += 1;
        const leitor = scannerRef.current;
        scannerRef.current = null;
        if (!leitor) return Promise.resolve();
        // stop() fecha a câmara e tira do #reader o vídeo, o canvas e a sombra.
        // (Sem clear(): podia apagar o vídeo de um arranque mais recente.)
        // Se ainda estiver a arrancar, stop() recusa; quando o arranque acabar,
        // vê a sessão mudada e desliga a câmara ele próprio.
        let parar = null;
        try { parar = leitor.stop(); } catch (e) { /* a arrancar ou já parado */ }
        return Promise.resolve(parar).catch(() => {});
    };

    // QR lido (pela câmara ou por fotografia): valida e abre o formulário.
    const processarQrLido = (decodedText) => {
        const parsed = parseAtcudClient(decodedText);
        // Os talões trazem às vezes outros QR (site, inquérito, MB WAY): o da
        // AT tem sempre o NIF do emissor (A) e o total (O).
        if (!parsed['A'] || parsed['O'] === undefined) {
            setQrErrorMsg('O QR Code lido não é o QR da AT. Aponte para o QR que tem os dados da fatura (normalmente perto do ATCUD, no fim do documento).');
            setScanResult(null);
            setQrNif('');
            setQrValor('');
            return;
        }
        const valor = parseFloat(parsed['O']) || 0;
        const nif = parsed['A'] || '';

        if (valor === 0) {
            setQrErrorMsg('O valor lido no QR Code é 0€.');
            setShowScanner(true);
            setScanResult(null);
            setQrNif('');
            setQrValor('');
            return;
        }

        setQrNif(nif);
        setQrValor(valor.toFixed(2));
        setScanResult(decodedText);

        // O campo F do QR é a data do documento, em AAAAMMDD.
        const f = parsed['F'] || '';
        procurarCartaoProvavel(valor, /^[0-9]{8}$/.test(f)
            ? `${f.slice(0, 4)}-${f.slice(4, 6)}-${f.slice(6, 8)}` : null);
    };

    // Constraints a reaplicar: sem width/height alguns browsers voltam aos 640×480.
    const aplicarAjustesCameraQr = (leitor, hd, focoContinuo, zoom) => {
        const avancado = [];
        if (focoContinuo) avancado.push({ focusMode: 'continuous' });
        if (zoom) avancado.push({ zoom });
        if (!avancado.length) return Promise.resolve();
        try {
            return leitor.applyVideoConstraints({ ...(hd ? QR_VIDEO_HD : {}), advanced: avancado })
                .catch((err) => console.warn('Ajustes da câmara não aplicados:', err));
        } catch (err) {
            return Promise.resolve(); // a câmara já foi desligada
        }
    };

    /* Depois de a câmara arrancar: foco contínuo e zoom, se o aparelho deixar.
       Devolve false se o browser ainda não indicar essas capacidades (o Chrome
       só as conhece um pouco depois de a câmara abrir). */
    const afinarCameraQr = (leitor, hd) => {
        let caps = {};
        try { caps = leitor.getRunningTrackCapabilities() || {}; } catch (e) { return true; }
        const focoContinuo = Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous');
        let zoom = null;
        const zMax = caps.zoom && caps.zoom.max;
        const zMin = (caps.zoom && caps.zoom.min) || 1;
        if (zMax && zMax >= 1.5) {
            const niveis = [...new Set([1, 2, 3].map(z => Math.min(Math.max(z, zMin), zMax)))];
            zoom = niveis.includes(2) ? 2 : niveis[niveis.length - 1];
            setQrZoom({ niveis, atual: zoom, hd, focoContinuo });
        }
        aplicarAjustesCameraQr(leitor, hd, focoContinuo, zoom);
        return focoContinuo || zoom !== null;
    };

    const trocarZoomQr = () => {
        const leitor = scannerRef.current;
        if (!leitor || !qrZoom) return;
        const i = qrZoom.niveis.indexOf(qrZoom.atual);
        const atual = qrZoom.niveis[(i + 1) % qrZoom.niveis.length];
        setQrZoom({ ...qrZoom, atual });
        aplicarAjustesCameraQr(leitor, qrZoom.hd, qrZoom.focoContinuo, atual);
    };

    const falharCameraQr = (msg) => {
        limparTemporizadoresQr();
        scannerRef.current = null;
        setIsScanning(false);
        setQrErrorMsg(msg);
    };

    const startScanner = (opcoes = {}) => {
        pararCameraQr();
        const sessao = qrSessaoRef.current;
        setQrErrorMsg('');
        setQrDica(false);
        setQrZoom(null);
        setQrModoFoto(false);
        setIsScanning(true);

        // getUserMedia só existe em https (ou localhost): por http não há câmara.
        if (window.isSecureContext === false) {
            falharCameraQr(`A câmara só funciona numa ligação segura (https). Está a usar ${window.location.origin}: abra a aplicação pelo endereço que começa por https://. Também pode fotografar o QR.`);
            return;
        }
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            falharCameraQr(mensagemErroCameraQr('mediaDevices not supported'));
            return;
        }

        const hd = !opcoes.simples;
        const arrancar = (tentativa = 0) => {
            if (sessao !== qrSessaoRef.current) return;
            const el = document.getElementById('reader');
            if (!el || !el.clientWidth) {
                if (tentativa < 40) setTimeout(() => arrancar(tentativa + 1), 50);
                else falharCameraQr('Não foi possível preparar o leitor. Volte atrás e tente de novo.');
                return;
            }
            let leitor;
            try {
                leitor = new Html5Qrcode('reader', QR_LEITOR_CONFIG);
            } catch (err) {
                console.error('Scanner Error:', err);
                falharCameraQr('Não foi possível preparar o leitor. Volte atrás e tente de novo.');
                return;
            }
            scannerRef.current = leitor;
            const config = {
                fps: 12,
                // caixa grande: o canvas descodificado tem o tamanho dela
                qrbox: (w, h) => {
                    const lado = Math.floor(Math.min(w, h) * 0.9);
                    return { width: lado, height: lado };
                },
                // um QR impresso nunca vem espelhado: tentar ao contrário só
                // gastava metade do tempo de cada imagem
                disableFlip: true,
                videoConstraints: { facingMode: { ideal: 'environment' }, ...(hd ? QR_VIDEO_HD : {}) },
            };

            let arranque;
            try {
                arranque = leitor.start(
                    { facingMode: 'environment' },
                    config,
                    (decodedText) => {
                        if (sessao !== qrSessaoRef.current) return;
                        pararCameraQr();
                        setIsScanning(false);
                        setQrDica(false);
                        processarQrLido(decodedText);
                    },
                    () => { /* frame sem QR: normal */ }
                );
            } catch (err) {
                arranque = Promise.reject(err);
            }
            arranque.then(() => {
                if (sessao !== qrSessaoRef.current) {
                    // a pessoa já saiu: desligar esta câmara
                    Promise.resolve().then(() => leitor.stop()).catch(() => {});
                    return;
                }
                const afinar = (n) => setTimeout(() => {
                    if (sessao !== qrSessaoRef.current) return;
                    if (!afinarCameraQr(leitor, hd) && n < 2) afinar(n + 1);
                }, n === 0 ? 500 : 1000);
                afinar(0);
                // O tempo conta a partir daqui, com a câmara já aberta (não
                // inclui o pedido de permissão).
                scannerTimeoutRef.current = setTimeout(() => setQrDica(true), QR_SEGUNDOS_DICA * 1000);
                qrDesligarRef.current = setTimeout(() => {
                    pararCameraQr();
                    setIsScanning(false);
                    setQrDica(false);
                    setQrErrorMsg('A câmara foi desligada ao fim de 2 minutos sem ler o QR Code. Pode voltar a tentar, fotografar o QR ou fazer o registo manual.');
                }, QR_SEGUNDOS_DESLIGAR * 1000);
            }).catch((err) => {
                if (sessao !== qrSessaoRef.current) return;
                scannerRef.current = null;
                // Alguns aparelhos recusam a resolução pedida: repetir sem ela.
                if (hd && /Overconstrained/i.test(String((err && err.name) || err))) {
                    startScanner({ simples: true });
                    return;
                }
                console.error('Scanner Error:', err);
                falharCameraQr(mensagemErroCameraQr(err));
            });
        };
        setTimeout(arrancar, 0);
    };

    const stopScanner = () => {
        pararCameraQr();
        setIsScanning(false);
        setQrDica(false);
        setQrModoFoto(false);
        setShowScanner(false);
    };

    /* «Fotografar o QR»: a câmara do próprio telemóvel foca melhor (e usa a
       macro nos iPhone Pro) e a fotografia é lida em resolução total. O clique
       no input tem de acontecer já, dentro do toque, senão o iOS não o abre. */
    const fotografarQr = () => {
        const input = qrFotoInputRef.current;
        if (input) { input.value = ''; input.click(); }
        pararCameraQr();
        setIsScanning(false);
        setQrDica(false);
        setQrErrorMsg('');
        setQrModoFoto(true);
    };

    const lerFotografiaQr = async (e) => {
        const input = e.target;
        const file = input.files && input.files[0];
        if (!file) return; // cancelou: fica o ecrã de espera com os botões
        // Se a pessoa sair do ecrã enquanto a fotografia é lida, o resultado
        // não pode reaparecer na próxima vez que abrir o leitor.
        const sessao = qrSessaoRef.current;
        setQrLendoFoto(true);
        try {
            const texto = await descodificarFotoQr(file);
            if (sessao !== qrSessaoRef.current) return;
            setQrModoFoto(false);
            processarQrLido(texto);
        } catch (err) {
            if (sessao !== qrSessaoRef.current) return;
            console.warn('QR não encontrado na fotografia:', err);
            setQrModoFoto(false);
            setQrErrorMsg('Não foi possível encontrar o QR Code na fotografia. Tire-a mais de perto, com o QR nítido, bem iluminado e sem reflexos, ou faça o registo manual.');
        } finally {
            setQrLendoFoto(false);
            input.value = '';
        }
    };

    const registoManualDoScanner = () => {
        pararCameraQr();
        setIsScanning(false);
        setQrDica(false);
        setQrModoFoto(false);
        setQrErrorMsg('');
        setManualEntry(true);
    };

    // Sair da página com a câmara ligada deixava-a acesa.
    useEffect(() => () => { pararCameraQr(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        e.target.value = ''; // permite voltar a escolher o mesmo ficheiro se se enganar
        if (!file) return;

        // PDF: guarda-se tal e qual — não há recorte nem OCR a fazer sobre ele
        if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
            setPhoto(file);
            setPhotoPdfNome(file.name);
            setPhotoPreview(null);
            return;
        }

        setPhotoPdfNome(null);
        const reader = new FileReader();
        reader.onloadend = () => {
            setPhoto(file);
            setPhotoPreview(reader.result);
            // A fotografia original segue diretamente para validação, sem recorte obrigatório.
        };
        reader.readAsDataURL(file);

        // Keep original file for OCR if needed
        if (manualEntry && window.Tesseract) {
            runOCR(file);
        }
    };

    const initCropper = (retries = 0) => {
        if (!cropImageRef.current) return;

        const CropperClass = window.Cropper?.default || window.Cropper;
        if (!CropperClass || typeof CropperClass !== 'function') {
            if (retries < 10) {
                setTimeout(() => initCropper(retries + 1), 300);
            }
            return;
        }

        try {
            if (cropperInstanceRef.current) {
                cropperInstanceRef.current.destroy();
            }
            cropperInstanceRef.current = new CropperClass(cropImageRef.current, {
                viewMode: 1,
                dragMode: 'move',
                aspectRatio: NaN,
                autoCropArea: 0.9,
                responsive: true,
                guides: true,
                background: false,
            });
        } catch (err) {
            console.warn('Cropper init failed, retrying...', err);
            if (retries < 10) {
                setTimeout(() => initCropper(retries + 1), 300);
            }
        }
    };

    const handleCropConfirm = () => {
        if (cropperInstanceRef.current) {
            const canvas = cropperInstanceRef.current.getCroppedCanvas({
                maxWidth: 2048,
                maxHeight: 2048,
            });
            const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.9);
            setPhotoPreview(croppedDataUrl);

            // Convert to file for upload
            canvas.toBlob((blob) => {
                const croppedFile = new File([blob], 'fatura-crop.jpg', { type: 'image/jpeg' });
                setPhoto(croppedFile);
            }, 'image/jpeg', 0.9);

            cropperInstanceRef.current.destroy();
            cropperInstanceRef.current = null;
        }
        setShowCropper(false);
        setCropImageSrc(null);
    };

    const handleCropCancel = () => {
        if (cropperInstanceRef.current) {
            cropperInstanceRef.current.destroy();
            cropperInstanceRef.current = null;
        }
        setShowCropper(false);
        setCropImageSrc(null);
    };

    const runOCR = async (file) => {
        setIsAnalyzing(true);
        try {
            const { data: { text } } = await window.Tesseract.recognize(file, 'por');

            // Only suggest ATCUD if user hasn't typed one
            if (!detectedAtcud) {
                const atcudMatch = text.match(/[A-Z0-9]{8}-[0-9]+/);
                if (atcudMatch) {
                    const fullAtcud = atcudMatch[0];
                    setDetectedAtcud(fullAtcud);
                    const prefix = fullAtcud.split('-')[0];

                    try {
                        const res = await api.get(`/despesas/lookup-prefix/${prefix}`);
                        if (res.data.success) {
                            setDetectedFornecedor(res.data.nif);
                        }
                    } catch (err) {
                        console.warn("Prefix lookup failed", err);
                    }
                }
            }

            // Only suggest valor if user hasn't typed one
            if (!valorTotal) {
                const lines = text.split('\n');
                for (let line of lines) {
                    if (line.toUpperCase().includes('TOTAL') || line.toUpperCase().includes('EUR') || line.toUpperCase().includes('PAGO')) {
                        const valMatch = line.match(/\d+[.,]\d{2}/);
                        if (valMatch) {
                            setValorTotal(valMatch[0].replace(',', '.'));
                            break;
                        }
                    }
                }
            }
            // A data do documento. Não entra no campo sozinha — fica como
            // sugestão por baixo dele, e é com ela que se confronta o que
            // estiver escrito antes de gravar.
            const lida = dataDoTalao(text);
            if (lida) {
                setDataOcr(lida);
                setDataConfirmada(false);
            }
        } catch (err) {
            console.error("OCR Error:", err);
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleDelete = async (id) => {
        if (!await liquidFeedback.confirm('Tem a certeza que deseja eliminar esta despesa?')) return;
        try {
            await api.delete(`/despesas/${id}`);
            fetchData();
            liquidFeedback.notify('Despesa eliminada!');
        } catch (err) {
            console.error(err);
            liquidFeedback.notify('Erro ao eliminar.');
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        /* O alvo é sempre a despesa aberta no modal. Havia aqui um
           "editingDespesa ||" alimentado pelo lápis dos Últimos Registos, que
           não abria ecrã nenhum e ficava pendurado: a edição seguinte, feita na
           lista, era gravada por cima da despesa anterior — com os valores dela,
           e com a fotografia nova colada ao talão errado. */
        const target = selectedDespesa;
        if (!target) return;
        try {
            const dados = {
                cartao_id: target.cartao_id,
                categoria_id: target.categoria_id,
                id_alocacao: target.id_alocacao,
                tipo: target.tipo || '',
                veiculo_id: target.veiculo_id || '',
                kms: target.kms ?? '',
                litros: target.litros ?? '',
                nif_emissor: target.nif_emissor,
                pais_nif: target.pais_nif ?? 'PT',
                numero_documento_completo: target.numero_documento_completo,
                valor_total: target.valor_total,
                data_documento: target.data_documento ? target.data_documento.substring(0, 10) : null,
                atcud: target.atcud
            };
            // EF: só nos cartões que a pedem (ausente = a ligação fica como está)
            if (cartoes.find(c => String(c.id) === String(target.cartao_id))?.pede_ef) {
                dados.ef_numero = target.ef_numero || '';
            }
            // Manutenção: só nos cartões com o visto (ausente = a marca fica como
            // está). Passar a despesa para um cartão sem o visto tira-lhe a marca.
            const original = despesas.find(x => x.id === target.id);
            if (opcoesRegisto?.cartoes?.[target.cartao_id]?.manutencao && opcoesRegisto?.projeto_empresa) {
                dados.manutencao = target.manutencao ? 'true' : 'false';
            } else if (target.manutencao && original && String(original.cartao_id) !== String(target.cartao_id)) {
                dados.manutencao = 'false';
            }
            let resp;
            if (novoComprovativo) {
                // anexo trocado: vai em multipart, com o ficheiro novo
                const fd = new FormData();
                Object.entries(dados).forEach(([k, v]) => fd.append(k, v ?? ''));
                fd.append('foto', novoComprovativo);
                resp = await api.put(`/despesas/${target.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
            } else {
                resp = await api.put(`/despesas/${target.id}`, dados);
            }
            setNovoComprovativo(null);
            setSelectedDespesa(null);
            setDetailEditing(false);
            fetchData();
            liquidFeedback.notify(resp?.data?.conciliada_revolut
                ? 'Atualizado! 🏦 Ficou cruzada com o movimento Revolut correspondente.'
                : 'Atualizado!');
        } catch (err) {
            console.error(err);
            liquidFeedback.notify(err.response?.data?.error || 'Erro ao atualizar.');
        }
    };

    // Grava só o projeto e a EF da despesa aberta. O servidor valida a EF e, com
    // o projeto vazio, põe o da EF — o que volta é o que fica nos campos.
    const guardarEfProjeto = async (valores) => {
        const alvo = selectedDespesa;
        if (!alvo) return;
        const projeto = String(valores.projeto ?? '').trim();
        const ef = String(valores.ef ?? '').trim();
        if (projeto === (alvo.id_alocacao || '') && ef === (alvo.ef_numero || '')) return;
        setEfProjeto(p => ({ ...p, estado: 'a guardar', erro: '' }));
        try {
            const r = await api.put(`/despesas/${alvo.id}`, { id_alocacao: projeto, ef_numero: ef });
            const d = r.data || {};
            const novos = {
                id_alocacao: d.id_alocacao ?? null,
                encomenda_fornecedor_id: d.encomenda_fornecedor_id ?? null,
                ef_numero: d.ef_numero ?? null,
                ef_fornecedor: d.ef_fornecedor ?? null,
            };
            setSelectedDespesa(s => (s && s.id === alvo.id ? { ...s, ...novos } : s));
            if (despesaAbertaRef.current === alvo.id) {
                setEfProjeto({ projeto: novos.id_alocacao || '', ef: novos.ef_numero || '', estado: 'guardado', erro: '' });
            }
            fetchData();
        } catch (err) {
            const msg = err.response?.data?.error || 'Não foi possível guardar o projeto e a EF.';
            // com o detalhe já fechado, o aviso não se perde
            if (despesaAbertaRef.current === alvo.id) setEfProjeto(p => ({ ...p, estado: '', erro: msg }));
            else liquidFeedback.notify(msg);
        }
    };

    // O cartão desta despesa pede EF e Projeto? (o das compras online)
    const cartaoPedeEf = (d) => !!cartoes.find(c => String(c.id) === String(d?.cartao_id))?.pede_ef;
    // O campo TIPO do detalhe. Nos cartões com EF fica ao lado do cartão, para
    // PROJETO e EF ficarem juntos na linha de baixo.
    const campoTipoDetalhe = () => (
        <div className="modal-field">
            <label>TIPO</label>
            {detailEditing ? (
                <select value={selectedDespesa.tipo || ''} onChange={(e) => setSelectedDespesa({ ...selectedDespesa, tipo: e.target.value })}>
                    <option value="">Geral</option>
                    <option value="combustivel">⛽ Combustível</option>
                </select>
            ) : (
                <p style={{ margin: 0, fontSize: 13, color: '#1e293b', fontWeight: 500 }}><LiquidText>{selectedDespesa.tipo === 'combustivel' ? '⛽ Combustível' : 'Geral'}</LiquidText></p>
            )}
        </div>
    );

    const validarRegisto = () => {
        if (aGuardarViatura) return false;
        if (tipoDespesa === 'combustivel' && novaViatura && formatarMatricula(novaViatura.matricula || '').length !== 8) {
            setNovaViatura(v => ({ ...v, erro: 'Indique a matrícula da viatura alugada, na forma AB-12-CD.' }));
            const campo = registoDespesaRef.current?.querySelector('[name="matricula_alugada"]');
            campo?.focus();
            campo?.scrollIntoView({ block: 'center' });
            return false;
        }
        // Também se valida ao confirmar a data, que não é um submit nativo.
        return registoDespesaRef.current?.reportValidity() ?? false;
    };

    const submitDespesa = async (force = false, viaturaGuardada = null) => {
        if (envioDespesaRef.current || !validarRegisto()) return;
        envioDespesaRef.current = true;
        setResultadoRegisto(null);
        let registoConfirmado = null;
        setSubmitting(true);
        let viaturaRegisto = viaturaGuardada || veiculoId;
        try {
            // O estado React só muda no render seguinte. Usar diretamente o ID
            // devolvido evita enviar a despesa ainda com a viatura em branco.
            if (tipoDespesa === 'combustivel' && novaViatura && !viaturaGuardada) {
                viaturaRegisto = await guardarViatura();
                if (!viaturaRegisto) return;
            }
            const formData = new FormData();

            if (manualEntry) {
                formData.append('atcud_string', detectedAtcud ? `H:${detectedAtcud}` : 'MANUAL_NO_ATCUD');
                formData.append('valor_manual', valorTotal);
                formData.append('is_manual', 'true');
                formData.append('data_manual', dataManual);
                formData.append('nif_manual', nifManual || detectedFornecedor || '');
                formData.append('pais_nif',paisNifManual);
                formData.append('numero_manual', numeroManual.trim());
            } else {
                formData.append('atcud_string', scanResult);
            }

            formData.append('cartao_id', selectedCartao);
            formData.append('utilizador_id', user.id);
            // «Manutenção»: o servidor põe o projeto da manutenção de quem regista
            const comoManutencao = paraManutencao && temManutencao;
            formData.append('id_alocacao', comoManutencao ? '' : idAlocacao);
            if (comoManutencao) formData.append('manutencao', 'true');
            formData.append('tipo', tipoDespesa);
            if (tipoDespesa === 'combustivel') {
                formData.append('veiculo_id', viaturaRegisto);
                formData.append('kms', kmsAbastecimento);
                formData.append('litros', litros);
            }
            if (force) formData.append('force', 'true');
            if (photo) formData.append('foto', photo);

            const resp = await api.post('/despesas', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000
            });

            if (resp.status !== 201 || !resp.data?.id) throw new Error('Resposta sem confirmação.');
            registoConfirmado = resp.data;
            setResultadoRegisto({ tipo: 'sucesso', despesa: resp.data });

            // A da manutenção não fica lembrada: escolhe-se compra a compra
            if (idAlocacao && !comoManutencao) {
                try { localStorage.setItem(`last_alocacao_${user?.id}_${selectedCartao}`, idAlocacao); } catch { /* Preferência local opcional. */ }
            }

            // A despesa fica registada mesmo que os kms sejam recusados pelo
            // histórico da viatura (conta-quilómetros não anda para trás)
            setTipoDespesa(''); setVeiculoId(''); setKmsAbastecimento(''); setLitros('');
            setParaManutencao(false);
            carregarOpcoesRegisto();
            setScanResult(null);
            setManualEntry(false);
            setValorTotal('');
            setDataManual('');
            setDataOcr('');
            setDataConfirmada(false);
            setPedirDataAgora(false);
            setConfereBanco(null);
            setDetectedAtcud('');
            setDetectedFornecedor(null);
            setNifManual(''); setPaisNifManual('PT'); setNumeroManual('');
            setQrNif('');
            setQrValor('');
            setPhoto(null);
            setPhotoPreview(null);
            setPhotoPdfNome(null);
            setShowScanner(false);
            const listaAtualizada = await fetchData();
            if (!listaAtualizada) setResultadoRegisto({ tipo: 'sucesso', despesa: registoConfirmado, avisoLista: true });
        } catch (err) {
            console.error(err);
            if (registoConfirmado) {
                setResultadoRegisto({ tipo: 'sucesso', despesa: registoConfirmado, avisoLista: true });
            } else if (err.response?.data?.code === 'COMPROVATIVO_QUALIDADE') {
                setResultadoRegisto({ tipo: 'erro', mensagem: err.response.data.error });
                // Conserva os campos para o colaborador apenas repetir a fotografia.
            } else if (err.response?.status === 409 && err.response?.data?.duplicate) {
                setResultadoRegisto({ tipo: 'erro', mensagem: 'Possível despesa duplicada. O registo foi bloqueado. Peça ao responsável que verifique o documento.' });
            } else {
                const recusado = err.response && err.response.status >= 400 && err.response.status < 500;
                setResultadoRegisto({
                    tipo: recusado ? 'erro' : 'incerto',
                    mensagem: recusado
                        ? (err.response?.data?.error || 'O servidor recusou o registo. Corrija os dados e tente novamente.')
                        : 'Não foi possível obter a confirmação do servidor. A despesa pode ter sido gravada. Consulte as despesas recentes antes de repetir o envio, para evitar duplicados.'
                });
            }
        } finally {
            envioDespesaRef.current = false;
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validarRegisto()) return;
        if ((!scanResult && !manualEntry) || !selectedCartao) {
            liquidFeedback.notify('Por favor, leia o código (ou selecione manual) e escolha um cartão.');
            return;
        }
        if (manualEntry && !dataManual) {
            liquidFeedback.notify('Indique a data da fatura (a data que está no talão).');
            const campo = document.getElementById('data-fatura');
            if (campo) { campo.focus(); campo.scrollIntoView({ block: 'center' }); }
            return;
        }
        /* No registo manual não há QR que dite a data: ela é escrita por quem
           lança, e o campo vem preenchido com hoje. Deixá-la passar por
           omissão é o que põe um talão de julho a contar em setembro — onde
           falta no fecho do mês certo e não encontra movimento no mês errado.
           Por isso a data tem de ser confirmada, sempre, e a confirmação diz
           em que mês a despesa vai cair. */
        if (manualEntry && !dataConfirmada) {
            setPedirDataAgora(true);
            /* Enquanto se lê a pergunta, vai-se saber se existe mesmo um
               pagamento deste valor neste mês. Se não houver e existir noutra
               data, é quase sempre porque a fatura é desse outro mês. */
            setConfereBanco(null);
            if (selectedCartao && valorTotal && dataManual) {
                api.get('/despesas/verificar-data', {
                    params: { cartao_id: selectedCartao, valor: valorTotal, data: dataManual },
                }).then(r => setConfereBanco(r.data)).catch(() => setConfereBanco(null));
            }
            return;
        }
        submitDespesa(false);
    };

    // ...

    // Default Card Logic
    const [defaultCardId, setDefaultCardId] = useState(localStorage.getItem(`default_card_${user?.id}`) || user?.cartao_habitual_id || '');

    useEffect(() => {
        if (defaultCardId) {
            setSelectedCartao(defaultCardId);
        } else if (cartoes.length > 0) {
            // Se não houver default, assume o primeiro
            setSelectedCartao(cartoes[0].id);
        }
    }, [defaultCardId, cartoes]);

    /* O campo abre com o último número usado neste cartão. O da manutenção não
       conta: escolhe-se compra a compra, e lembrado mandava as compras seguintes
       para a manutenção sem ninguém dar por isso. Nesse caso abre com o último
       projeto que o servidor conhece para este cartão. */
    // A opção segue o cartão escolhido: aparece nos cartões com o visto
    // «Manutenção» (Trabalhadores → Cartões Associados). O número é o projeto da
    // manutenção dos Dados da Empresa, e quem o decide ao gravar é o servidor.
    const opcaoCartao = opcoesRegisto?.cartoes?.[selectedCartao];
    const projetoManutencao = opcaoCartao?.manutencao ? (opcoesRegisto?.projeto_empresa || null) : null;
    const ultimoProjetoCartao = opcaoCartao?.ultimo_projeto || opcoesRegisto?.ultimo_projeto?.[selectedCartao] || '';
    const temManutencao = !!projetoManutencao;
    useEffect(() => {
        if (selectedCartao) {
            const lastAlocado = localStorage.getItem(`last_alocacao_${user?.id}_${selectedCartao}`);
            if (lastAlocado && lastAlocado === projetoManutencao) {
                setIdAlocacao(ultimoProjetoCartao);
            } else if (lastAlocado) {
                setIdAlocacao(lastAlocado);
            } else {
                setIdAlocacao('');
            }
        }
    }, [selectedCartao, user?.id, projetoManutencao, ultimoProjetoCartao]);

    const handleDefaultCardChange = async (e) => {
        const cardId = e.target.value;
        setDefaultCardId(cardId);
        setSelectedCartao(cardId);
        localStorage.setItem(`default_card_${user?.id}`, cardId);

        try {
            await api.put('/auth/me/cartao', { cartao_habitual_id: cardId });
        } catch (err) {
            console.error('Erro ao guardar cartão habitual:', err);
        }
    };

    return (
        <div className={`despesas-page ${captureMode ? 'capture-mode-active' : ''}`}>
            {/* upload de fatura para um movimento Revolut (câmara no telemóvel) */}
            <input ref={revFileRef} type="file" accept="image/*,application/pdf" capture="environment"
                onChange={enviarFaturaRevolut} style={{ display: 'none' }} />
            {/* «Fotografar o QR»: câmara do telemóvel; a fotografia é lida em #reader-foto */}
            <input ref={qrFotoInputRef} type="file" accept="image/*" capture="environment"
                onChange={lerFotografiaQr} style={{ display: 'none' }} />
            <div id="reader-foto" style={{ display: 'none' }} />
            {/* ... top bar ... */}
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1><OrcamentoIcon name="card" fallback="📝" /> Despesas</h1>
                        {!captureMode && <p className="mobile-hide">Gestão e Controlo de Faturas</p>}
                    </div>
                </div>
                {!captureMode ? (
                    <div className="top-bar-actions">
                        <div className="export-actions mobile-compact">
                            {/* Perguntam sempre o mês antes de exportar: seguirem em
                                silêncio o filtro do ecrã fazia descarregar setembro a
                                quem queria o fecho de agosto. "do mês" só em ecrã
                                largo — no telemóvel os quatro botões vão na mesma
                                fila, que não quebra linha. */}
                            <button className="export-btn large"
                                onClick={() => setExportar({ tipo: 'excel', mes: Number(filterMes), ano: Number(filterAno), cartao_id: filterCartao })}
                                title="Excel das despesas de um mês — escolhe-se o mês a seguir.">
                                <OrcamentoIcon name="chart" fallback="📊" /> Excel<span className="mobile-hide"> do mês</span>
                            </button>
                            <button className="export-btn large" disabled={zipBusy}
                                onClick={() => setExportar({ tipo: 'zip', mes: Number(filterMes), ano: Number(filterAno), cartao_id: filterCartao, cartao_ids: cartoes.filter(c => /\bBCP\b/i.test(c.nome || '')).map(c => String(c.id)) })}
                                title="ZIP com as fotografias e o Excel de um mês — escolhe-se o mês a seguir. Pode demorar.">
                                {zipBusy ? <LiquidText>⏳ A preparar…</LiquidText> : <><LiquidText>📦 ZIP</LiquidText><span className="mobile-hide"> do mês</span></>}
                            </button>
                            <button className="export-btn large" onClick={sincronizarRevolut} disabled={revBusy} title="Importar movimentos dos cartões Revolut">
                                <LiquidText>{revBusy ? '⏳ …' : '🏦 Sincronizar'}</LiquidText>
                            </button>
                            <button className="export-btn large" onClick={() => navigate('/revolut')} title="Página Revolut: conciliação avançada, ignoradas, histórico">
                                <OrcamentoIcon name="settings" fallback="⚙️" /> Revolut
                            </button>
                        </div>
                        <button className="btn-primary" onClick={() => setCaptureMode(true)}>
                            + <span className="mobile-hide">Nova Despesa</span><span className="mobile-only">Registar</span>
                        </button>
                    </div>
                ) : (
                    <div className="top-bar-actions">
                        {/* No telemóvel, voltar é sempre para o menu principal — a gestão
                            da lista é trabalho de PC. No PC, volta-se à lista (o registo
                            abriu-se a partir dela com "+ Nova Despesa"). */}
                        {(isMobileUser || ecraPequeno()) ? (
                            <button className="btn-secondary" onClick={() => navigate('/mobile-dashboard')}>
                                ← Menu
                            </button>
                        ) : (
                            <button className="btn-secondary" onClick={() => setCaptureMode(false)}>
                                ← Voltar
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="page-content">
                {captureMode ? (
                    <div className="capture-view">
                        <div className="capture-hero">
                            {/* Card Selector */}
                            <div className="default-card-selector">
                                <label>Cartão Selecionado</label>
                                <select value={defaultCardId} onChange={handleDefaultCardChange} className="card-dropdown">
                                    <option value="">Selecione um cartão habitual...</option>
                                    {cartoes.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.nome} {c.numero_mascarado ? `(${c.numero_mascarado})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Manual Entry Toggle Button */}
                            <div style={{ marginBottom: '20px' }}>
                                <button
                                    onClick={() => {
                                        const newState = !manualEntry;
                                        setManualEntry(newState);
                                        if (newState) { setShowScanner(true); } else { setShowScanner(false); }
                                    }}
                                    className={`btn-manual-toggle ${manualEntry ? 'active' : ''}`}
                                    style={{ width: '100%', padding: '17px', borderRadius: '12px', border: '1px solid #e2e8f0', background: manualEntry ? '#fffaf0' : 'white', fontWeight: '600', color: manualEntry ? '#dd6b20' : '#4a5568', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}
                                >
                                    <span><LiquidText>{manualEntry ? '✅' : '⬜'}</LiquidText></span> Registo Sem ATCUD (Manual)
                                </button>
                            </div>

                            {!manualEntry && (
                                <div className="capture-main-card" onClick={() => { setShowScanner(true); startScanner(); }}>
                                    <div className="camera-icon-wrapper">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                                            <circle cx="12" cy="13" r="4"></circle>
                                        </svg>
                                        <div className="camera-ring"></div>
                                    </div>
                                    <div className="capture-text">
                                        <h2>Ler QR Code</h2>
                                        <p>Toque para abrir a câmara</p>
                                    </div>
                                </div>
                            )}

                            {manualEntry && (
                                <div className="capture-main-card manual-active" onClick={() => setShowScanner(true)}>
                                    <div className="camera-icon-wrapper" style={{ background: '#ed8936' }}><span className="camera-emoji"><OrcamentoIcon name="document" fallback="📝" /></span></div>
                                    <div className="capture-text">
                                        <h2>Preencher Manualmente</h2>
                                        <p>Toque para inserir dados</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Recent Entries Section */}
                        {despesas.length > 0 && (
                            <div className="recent-entries-section">
                                <h3 style={{ fontSize: '1.1rem', color: '#2d3748', marginBottom: '15px' }}>Últimos Registos</h3>
                                <div className="recent-list">
                                    {despesas.slice(0, 3).map(d => (
                                        <div data-liquid-surface="true" key={d.id} className="recent-item" style={{ background: 'white', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                                <div>
                                                    <div style={{ fontSize: '0.85rem', color: '#2b6cb0', fontWeight: '700', marginBottom: '2px' }}>Fatura: {d.data_documento ? new Date(d.data_documento).toLocaleDateString() : '-'}</div>
                                                    <div style={{ fontSize: '0.75rem', color: '#718096', fontWeight: '600' }}>Carga: {new Date(d.criado_em).toLocaleDateString()}</div>
                                                    <div style={{ fontWeight: '600', color: '#2d3748', marginTop: '6px' }}>{d.nif_emissor}</div>
                                                </div>
                                                <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#2b6cb0' }}>{fmtMoney(d.valor_total)}</div>
                                            </div>
                                            {/* Só apagar. O ✏️ Editar que aqui esteve não abria nada —
                                                deixava a despesa presa em memória e a edição seguinte
                                                ia parar a esta. Para corrigir um registo, abre-se na
                                                lista, que é onde o formulário existe. */}
                                            <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid #f7fafc', paddingTop: '10px' }}>
                                                <button className="action-btn-delete" onClick={() => handleDelete(d.id)} style={{ flex: 1, justifyContent: 'center' }}><OrcamentoIcon name="trash" fallback="🗑️" /> Apagar</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Barra única: a lista de despesas e as secções da reconciliação
                            de extratos, todas ao mesmo nível. Só no computador — no
                            telemóvel/tablet a página mantém-se como sempre foi, que a
                            reconciliação de extratos é trabalho de secretária. */}
                        {/* Nomeia os meses e os cartões, que é o que permite decidir sem
                            abrir nada. Só na lista: desenhada por cima dos sete ecrãs do
                            reconciliador, era o mesmo aviso laranja de cada vez que se lá
                            entrava — e um aviso permanente deixa de se ler. */}
                        {!isMobileUser && tardiosTotalMeses > 0 && painel === 'despesas' && (
                            <div className="tardios-faixa">
                                <span className="tardios-faixa-icone"><LiquidText>⏰</LiquidText></span>
                                <div className="tardios-faixa-texto">
                                    {tardiosMeses.slice(0, 3).map(m => (
                                        <div key={`${m.ano}-${m.mes}`}>
                                            <strong>
                                                {new Date(0, m.mes - 1).toLocaleString('pt-PT', { month: 'long' })}/{m.ano}
                                            </strong>
                                            {': '}{m.total} despesa(s) carregada(s) depois do fecho
                                            {m.cartoes?.length ? ` — ${m.cartoes.join(', ')}` : ''}
                                        </div>
                                    ))}
                                    {tardiosTotalMeses > 3 && <div>…e mais {tardiosTotalMeses - 3} mês(es).</div>}
                                </div>
                                <button className="tardios-faixa-btn" onClick={() => setPainel('tardios')}>
                                    Ver detalhe
                                </button>
                            </div>
                        )}

                        {!isMobileUser && (
                        <div className="shared-subnav subnav-reconciliador" style={{ marginBottom: 16, flexWrap: 'nowrap', overflowX: 'auto' }}>
                            {/* Cada separador em duas linhas — a primeira palavra em cima,
                                o resto em baixo — para caberem todos na mesma fila em vez
                                de a segunda metade passar para uma segunda linha.
                                "Faturas s/ extrato" saiu: lia a mesma origem das Pendências
                                e dava as mesmas duas saídas. O que só ele tinha — a lista
                                pronta a copiar, com o NIF e o subtotal por cartão e mês —
                                passou para as Pendências. A página do reconciliador fica
                                de pé (o menu dele ainda lhe aponta). */}
                            {[
                                ['despesas', '🧾', 'Despesas e', 'Movimentos'],
                                ['/reconciliacoes', '💳', 'Cartões', 'reconciliados'],
                                ['/upload_mensal', '⬆️', 'Upload', 'ficheiros'],
                                ['/download', '⬇️', 'Download', 'ficheiros'],
                                ['/associar_tardia', '🔗', 'Faturas à', 'posteriori'],
                                ['/pendencias', '⏳', 'Pendências', ''],
                                ['/listagens', '⚠️', 'Cartões', 's/ faturas'],
                                ['/impressao', '🖨️', 'Impressão', 'extr/fcts'],
                                ['tardios', '⏰', 'Registos', `tardios${tardiosTotalMeses > 0 ? ` (${tardiosTotalMeses})` : ''}`],
                            ].map(([chave, icone, cima, baixo]) => (
                                <button key={chave}
                                    className={`shared-subnav-btn subnav-btn-duplo ${painel === chave ? 'active' : ''}`}
                                    onClick={() => { setPainel(chave); setPainelClique(n => n + 1); }}>
                                    {/* O ícone sai de dentro da primeira linha e passa a
                                        ocupar uma coluna só dele. Enquanto estava lá dentro,
                                        empurrava o texto de cima para a direita e as duas
                                        linhas nunca podiam alinhar uma com a outra. */}
                                    <span className="subnav-icone"><OrcamentoIcon name={{ despesas: 'document', '/reconciliacoes': 'briefcase', '/upload_mensal': 'upload', '/download': 'download', '/associar_tardia': 'copy', '/pendencias': 'clock', '/listagens': 'warning', '/impressao': 'print', tardios: 'clock' }[chave]} fallback={icone} /></span>
                                    {/* Havendo só uma palavra, ela ocupa a altura das duas
                                        linhas e fica a meio, em vez de assentar na primeira
                                        com uma linha vazia por baixo. */}
                                    <span className={baixo ? '' : 'subnav-unico'}>{cima}</span>
                                    {baixo && <span className="subnav-linha2">{baixo}</span>}
                                </button>
                            ))}
                        </div>
                        )}

                        {painel === 'tardios' && !isMobileUser ? <RegistosTardios />
                        : painel !== 'despesas' && !isMobileUser ? <Reconciliador embutido rota={painel} nonce={painelClique} /> : (
                        <>
                        <div className="filters-bar">
                            {/* ... Filters content ... */}
                            <div className="filter-group">
                                <label>Mês</label>
                                <select value={filterMes} onChange={(e) => setFilterMes(e.target.value)}>
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                        <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('pt-PT', { month: 'long' })}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="filter-group">
                                <label>Ano</label>
                                <select value={filterAno} onChange={(e) => setFilterAno(e.target.value)}>
                                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="filter-group">
                                <label>Fornecedor</label>
                                <input
                                    type="text"
                                    placeholder="Nome ou NIF..."
                                    value={filterFornecedor}
                                    onChange={(e) => setFilterFornecedor(e.target.value)}
                                />
                            </div>
                            <div className="filter-group">
                                <label>Cartão</label>
                                <select value={filterCartao} onChange={(e) => setFilterCartao(e.target.value)}>
                                    <option value="">Todos</option>
                                    {cartoes.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.nome} {c.numero_mascarado ? `(${c.numero_mascarado})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {/* Limpa também os funis das colunas. Havia dois botões
                                "Limpar" no mesmo ecrã, cada um a limpar metade, e o de
                                cima deixava a lista filtrada por coluna sem o dizer. */}
                            <button className="btn-secondary" onClick={() => { setFilterMes(new Date().getMonth() + 1); setFilterAno(new Date().getFullYear()); setFilterCartao(''); setFilterFornecedor(''); limparColunas(); }}>
                                Limpar tudo
                            </button>
                            <button className="btn-secondary" onClick={() => setShowCardManager(true)}><OrcamentoIcon name="card" fallback="💳" /> Cartões</button>
                        </div>

                        {/* O alcance da exportação escrito no ecrã, e não só no title dos
                            botões: no telemóvel não há rato para passar por cima e esta caixa
                            de filtros também lá é desenhada. Fica aqui, colado aos filtros,
                            porque é a filtrar que nasce a ideia errada de que o ficheiro sai
                            filtrado. */}
                        <p className="export-nota-alcance"><LiquidText>
                            ℹ️ O </LiquidText><strong>Excel</strong> e o <strong>ZIP</strong> perguntam o mês antes de exportar
                            e levam sempre o mês inteiro, sem o fornecedor nem os filtros das colunas.
                        </p>

                        {!isMobileUser && (
                            <>
                                <div className="summary-bar compact">
                                    {/* "Total Filtrado" dava a entender que era o
                                        total do mês, e não é: só entra o que passou em
                                        cartões. As faturas de fornecedor pagas por
                                        transferência não aparecem aqui, e em julho de
                                        2026 eram 468.628 EUR. */}
                                    {/* Todas estas colunas vêm dos EXTRATOS, e só dos extratos.
                                        É o que as torna comparáveis entre si e o que as faz
                                        fechar: com fatura + sem fatura = gasto do mês.
                                        O gest tem a sua própria noção de "com fatura" (a despesa
                                        lançada) que não coincide com a do reconciliador (a fatura
                                        anexada ao movimento) — misturá-las daria números a mudar
                                        sozinhos quando o extrato fosse carregado. */}
                                    {gastoCartoes && (
                                        <div className="summary-item gasto-mes"
                                            title={gastoCartoes.cartoes_com_extrato === 0
                                                ? 'Ainda não foi carregado nenhum extrato deste mês.'
                                                : 'Soma das linhas dos extratos do mês. É o dinheiro que saiu do banco.'}>
                                            <span className="summary-label">Total<br />Cartões</span>
                                            <span className="summary-value">
                                                {gastoCartoes.cartoes_com_extrato > 0 ? (
                                                    <>
                                                        {fmtMoney(gastoCartoes.gasto)}
                                                        {/* Quantos movimentos deram este valor: o total
                                                            sozinho não diz se são dez compras grandes
                                                            ou quinhentas pequenas. */}
                                                        <span className="summary-sub">
                                                            {gastoCartoes.total_fatura.com_n + gastoCartoes.total_fatura.sem_n} movimentos
                                                        </span>
                                                        {/* O total vem da soma dos extratos; as colunas com/sem
                                                            vêm dos movimentos, um a um. Fecham sempre — e quando
                                                            não fecharem, é aqui que tem de aparecer. */}
                                                        {Math.abs(gastoCartoes.diferenca || 0) > 0.01 && (
                                                            <span className="summary-sub parcial" title="A soma dos extratos não bate com com fatura + sem fatura. Verifica os movimentos por classificar.">
                                                                não fecha por {fmtMoney(Math.abs(gastoCartoes.diferenca))}
                                                            </span>
                                                        )}
                                                    </>
                                                ) : revolutMes ? (
                                                    <>
                                                        {fmtMoney(revolutMes.valor)}
                                                        <span className="summary-sub">{revolutMes.movimentos} movimentos</span>
                                                        <span className="summary-sub parcial">só cartões Revolut</span>
                                                    </>
                                                ) : (
                                                    <span className="sem-extratos">Extratos ainda<br />não carregados</span>
                                                )}
                                            </span>
                                        </div>
                                    )}
                                    {/* Mês corrente: as mesmas sete colunas, no mesmo sítio, com
                                        a mesma leitura. O que muda é a sinalização — o BCP diz
                                        que falta o extrato, e o que só cobre a Revolut diz que
                                        é parcial.

                                        A repartição vem do gest porque no mês corrente é a única
                                        medida de justificação que existe: ainda não há
                                        reconciliação nenhuma. Quando o extrato entrar, o
                                        reconciliador começa por cruzar com estas mesmas despesas
                                        e os números coincidem nesse momento; afastam-se depois,
                                        à medida que se justifica de um lado ou do outro. */}
                                    {gastoCartoes && gastoCartoes.cartoes_com_extrato === 0 && revolutMes && [
                                        ['BCP', 'C/Fatura', null, null, false],
                                        ['BCP', 'S/Fatura', null, null, false],
                                        ['Revolut', 'C/Fatura', revolutMes.com_n, revolutMes.com_v, false],
                                        ['Revolut', 'S/Fatura', revolutMes.sem_n, revolutMes.sem_v, false],
                                        ['Movimentos', 'C/Fatura', revolutMes.com_n, revolutMes.com_v, true],
                                        ['Movimentos', 'S/Fatura', revolutMes.sem_n, revolutMes.sem_v, true],
                                    ].map(([cima, baixo, n, v, parcial]) => (
                                        <div className="summary-item" key={cima + baixo}>
                                            <span className="summary-label">{cima}<br />{baixo}</span>
                                            <span className="summary-value">
                                                {n === null ? (
                                                    <span className="sem-extratos">Extratos ainda<br />não carregados</span>
                                                ) : (
                                                    <>
                                                        {n}
                                                        <span className="summary-sub">{fmtMoney(v)}</span>
                                                        {parcial && <span className="summary-sub parcial">só cartões Revolut</span>}
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                    ))}
                                    {gastoCartoes && gastoCartoes.cartoes_com_extrato > 0 && [
                                        ['BCP', 'C/Fatura', gastoCartoes.bcp.com_n, gastoCartoes.bcp.com_v],
                                        ['BCP', 'S/Fatura', gastoCartoes.bcp.sem_n, gastoCartoes.bcp.sem_v],
                                        ['Revolut', 'C/Fatura', gastoCartoes.revolut.com_n, gastoCartoes.revolut.com_v],
                                        ['Revolut', 'S/Fatura', gastoCartoes.revolut.sem_n, gastoCartoes.revolut.sem_v],
                                        ['Movimentos', 'C/Fatura', gastoCartoes.total_fatura.com_n, gastoCartoes.total_fatura.com_v],
                                        ['Movimentos', 'S/Fatura', gastoCartoes.total_fatura.sem_n, gastoCartoes.total_fatura.sem_v],
                                    ].map(([cima, baixo, n, v]) => (
                                        <div className="summary-item" key={cima + baixo}>
                                            <span className="summary-label">{cima}<br />{baixo}</span>
                                            <span className="summary-value">
                                                {n}
                                                <span className="summary-sub">{fmtMoney(v)}</span>
                                            </span>
                                        </div>
                                    ))}
                                    {haFiltro && (
                                        <div className="summary-item lista-filtrada">
                                            <span className="summary-label">Lista filtrada</span>
                                            <span className="summary-value">
                                                <button type="button" className="btn-limpar-filtros" onClick={limparColunas}>
                                                    Limpar filtros de coluna
                                                </button>
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Um extrato em falta e um cartão sem movimentos parecem
                                    a mesma coisa num total, e não são. O Revolut agrava
                                    isto: o extrato é um só ficheiro para todos os cartões,
                                    por isso dizer "falta o extrato deste cartão" mandaria
                                    procurar um ficheiro que não existe. */}
                                {gastoCartoes && (() => {
                                    const falta = gastoCartoes.sem_extrato || [];
                                    const bcp = falta.filter(c => !c.revolut);
                                    const rev = falta.filter(c => c.revolut);
                                    // Enquanto o mês não fechou, faltarem extratos é o
                                    // estado normal. Avisar aqui seria avisar todos os
                                    // dias, e um aviso permanente deixa de se ler.
                                    const exigivel = gastoCartoes.esperado_completo;
                                    if (!falta.length) return null;
                                    return (
                                        <>
                                            {falta.length > 0 && exigivel && (
                                                <div className="aviso-sem-extrato">
                                                    <span className="aviso-icone"><OrcamentoIcon name="warning" fallback="⚠️" /></span>
                                                    <div>
                                                        <strong>Faltam extratos neste mês</strong>
                                                        <span className="aviso-detalhe">
                                                            O valor acima não os inclui.
                                                        </span>
                                                        <ul className="aviso-lista">
                                                            {rev.length > 0 && (
                                                                <li key="revolut">
                                                                    Extrato Revolut do mês por carregar
                                                                    <span className="aviso-digitos">
                                                                        — é um só ficheiro, abrange {rev.length} cartões
                                                                    </span>
                                                                </li>
                                                            )}
                                                            {bcp.map(c => (
                                                                <li key={c.id}>
                                                                    {c.nome} <span className="aviso-digitos">{c.digitos}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}

                            </>
                        )}

                        <LoteMensagens tipo="movimentos" escolhidas={escolhidosMov}
                            onLimpar={() => setEscolhidosMov(new Set())}
                            onEnviado={(ids) => {
                                setEscolhidosMov(p => { const s = new Set(p); ids.forEach(i => s.delete(i)); return s; });
                                fetchData();
                            }} />

                        <LoteMensagens escolhidas={escolhidas}
                            onLimpar={() => setEscolhidas(new Set())}
                            onEnviado={(ids) => {
                                setEscolhidas(p => { const s = new Set(p); ids.forEach(i => s.delete(i)); return s; });
                                fetchData();
                            }} />

                        {despesasFiltradas.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '100px 20px', color: '#718096' }}>Nenhuma despesa encontrada para este período.</div>
                        ) : (
                            (
                                <div data-liquid-surface="true" className="table-container" style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                    <table className="despesas-table standard-table">
                                        <thead><tr>
                                            {/* Pedir a fatura em falta. Antes do Estado porque é o
                                                próprio estado — "sem fatura" — que a torna aplicável.
                                                São duas caixas: a da esquerda apanha o dia todo, a da
                                                direita um movimento só. */}
                                            <th style={{ width: 24, textAlign: 'center', padding: '8px 2px 8px 12px' }}
                                                title="Escolher todos os movimentos desse dia"><LiquidText>✕</LiquidText></th>
                                            <th style={{ width: 24, textAlign: 'center', padding: '8px 2px' }}
                                                title="Escolher para pedir a fatura ao titular do cartão"><LiquidText>✕</LiquidText></th>
                                            <CabecalhoCol campo="estado" titulo="Estado" ordem={ordem} aoOrdenar={ordenarPor}
                                                style={{ width: 76, textAlign: 'center' }}
                                                filtro={<FiltroColuna titulo="Estado" valores={valoresDe('estado')}
                                                    escolhidos={escolhidos('estado')} aoMudar={v => porColuna('estado', v)} />} />
                                            <th style={{ width: 60, textAlign: 'center' }}>Fatura</th>
                                            <th style={{ width: 46, textAlign: 'center' }} title="M = despesa carregada como manutenção">
                                                <FiltroColuna titulo="Manutenção" valores={valoresDe('manutencao')}
                                                    escolhidos={escolhidos('manutencao')} aoMudar={v => porColuna('manutencao', v)} />
                                            </th>
                                            <CabecalhoCol campo="data" titulo="Data" ordem={ordem} aoOrdenar={ordenarPor}
                                                filtro={<FiltroColuna titulo="Data" valores={valoresDe('data')}
                                                    escolhidos={escolhidos('data')} aoMudar={v => porColuna('data', v)} />} />
                                            <th style={{ width: 32, textAlign: 'center' }}
                                                title="Escolher para enviar mensagem ao colaborador"><LiquidText>✕</LiquidText></th>
                                            <CabecalhoCol campo="fornecedor" titulo="Fornecedor / Comerciante" ordem={ordem} aoOrdenar={ordenarPor}
                                                filtro={<FiltroColuna titulo="Fornecedor" valores={valoresDe('fornecedor')}
                                                    escolhidos={escolhidos('fornecedor')} aoMudar={v => porColuna('fornecedor', v)} />} />
                                            <CabecalhoCol campo="numero" titulo="Nº Fatura" ordem={ordem} aoOrdenar={ordenarPor}
                                                filtro={<FiltroColuna titulo="Nº Fatura" valores={valoresDe('numero')}
                                                    escolhidos={escolhidos('numero')} aoMudar={v => porColuna('numero', v)} />} />
                                            <CabecalhoCol campo="cartao" titulo="Cartão" ordem={ordem} aoOrdenar={ordenarPor}
                                                filtro={<FiltroColuna titulo="Cartão" valores={valoresDe('cartao')}
                                                    escolhidos={escolhidos('cartao')} aoMudar={v => porColuna('cartao', v)} />} />
                                            {/* Quem tinha sessão iniciada quando a despesa foi
                                                registada. O nome do cartão diz de quem ele é, não
                                                quem fez a compra — e é a esta pessoa que o aviso
                                                de correção é enviado. */}
                                            <CabecalhoCol campo="login" titulo="Login app" ordem={ordem} aoOrdenar={ordenarPor}
                                                filtro={<FiltroColuna titulo="Login app" valores={valoresDe('login')}
                                                    escolhidos={escolhidos('login')} aoMudar={v => porColuna('login', v)} />} />
                                            <CabecalhoCol campo="total" titulo="Total" ordem={ordem} aoOrdenar={ordenarPor} />
                                            <th style={{ textAlign: 'center' }}>Ações</th>
                                        </tr></thead>
                                        <tbody>
                                            {despesasFiltradas.map(d => {
                                                const movimentoRevolut = d.origem === 'revolut';
                                                const verificacao = ehVerificacaoCartao(d);
                                                // Já justificado no reconciliador não é trabalho por
                                                // fazer: não se pinta de laranja nem se pede a fatura.
                                                const semFatura = movimentoRevolut && !verificacao && !d.justificada_recon;
                                                return (
                                                <tr key={d.id} onClick={() => !movimentoRevolut && setSelectedDespesa(d)} style={{ cursor: movimentoRevolut ? 'default' : 'pointer', background: semFatura ? '#fff7ed' : 'transparent' }}>
                                                    {/* Pedir a fatura: só faz sentido num movimento que
                                                        ainda não tem talão lançado. Onde já há fatura, ou
                                                        onde o reconciliador já o deu por justificado, não
                                                        há nada a pedir. */}
                                                    {/* A caixa do dia só se desenha uma vez por dia,
                                                        na primeira linha em que ele aparece. */}
                                                    <td style={{ textAlign: 'center', padding: '8px 2px 8px 12px' }} onClick={e => e.stopPropagation()}>
                                                        <div style={CELULA_CAIXA}>
                                                            {diasMov.primeira.has(d.transacao_id) ? (() => {
                                                                const dia = diasMov.primeira.get(d.transacao_id);
                                                                const ids = diasMov.porDia.get(dia) || [];
                                                                return <input type="checkbox" style={CAIXA}
                                                                    checked={ids.every(i => escolhidosMov.has(i))}
                                                                    onChange={() => alternarDia(dia)}
                                                                    title={`Escolher ${ids.length === 1 ? 'o movimento' : `os ${ids.length} movimentos`} de ${dia.split('-').reverse().join('/')}`} />;
                                                            })() : <span style={{ color: '#e2e8f0' }}>·</span>}
                                                        </div>
                                                    </td>
                                                    <td style={{ textAlign: 'center', padding: '8px 2px' }} onClick={e => e.stopPropagation()}>
                                                        {/* Sempre disponível em qualquer movimento sem
                                                            fatura: no envio manual quem decide o que vale
                                                            a pena pedir é quem envia, não uma regra de
                                                            idade. O corte por dias fica para quando houver
                                                            envio automático. */}
                                                        {!semFatura || !d.transacao_id ? (
                                                            <div style={CELULA_CAIXA}><span style={{ color: '#e2e8f0' }}>·</span></div>
                                                        ) : (
                                                            <div style={CELULA_CAIXA}>
                                                                <input type="checkbox" style={CAIXA} checked={escolhidosMov.has(d.transacao_id)}
                                                                    onChange={() => alternarMov(d.transacao_id)}
                                                                    title="Escolher para pedir a fatura ao titular do cartão" />
                                                                {movAvisados[d.transacao_id]?.enviado_em && (
                                                                    <HistoricoEnvios transacaoId={d.transacao_id}
                                                                        quantos={movAvisados[d.transacao_id].envios || 1} />
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    {/* Estado: ⚠️ por justificar · ✅ tratado */}
                                                    <td style={{ textAlign: 'center', fontSize: '1.15rem' }}>
                                                        {verificacao
                                                            ? <span title="Verificação de cartão (0,00 €) — não gera fatura"><OrcamentoIcon name="check" fallback="✅" /></span>
                                                            : d.justificada_recon
                                                                ? <span title="Fatura já anexada no reconciliador — não é preciso pedir"><OrcamentoIcon name="document" fallback="🗂️" /></span>
                                                                : semFatura
                                                                    ? <span title="Movimento Revolut sem fatura — falta justificar"><OrcamentoIcon name="warning" fallback="⚠️" /></span>
                                                                    : d.caminho_foto
                                                                    ? <span title="Fatura registada com imagem"><OrcamentoIcon name="check" fallback="✅" /></span>
                                                                    : <span title="Fatura registada, sem imagem anexada"><LiquidText>🟡</LiquidText></span>}
                                                    </td>
                                                    {/* Imagem da fatura */}
                                                    <td style={{ textAlign: 'center' }}>
                                                        {!semFatura && d.caminho_foto
                                                            ? <ComprovativoImg despesaId={d.id} miniatura versao={d.caminho_foto} style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} />
                                                            : <span style={{ color: '#cbd5e0' }}>—</span>}
                                                    </td>
                                                    {/* M: despesa carregada como manutenção */}
                                                    <td style={{ textAlign: 'center' }}>
                                                        {d.manutencao && (
                                                            <span title="Despesa carregada como manutenção"
                                                                style={{ display: 'inline-block', minWidth: 22, padding: '2px 6px', borderRadius: 6,
                                                                    background: '#ebf8ff', border: '1px solid #90cdf4', color: '#2b6cb0',
                                                                    fontWeight: 800, fontSize: '0.85rem', lineHeight: 1.3 }}>
                                                                M
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <strong style={{ color: '#2b6cb0' }}>{d.data_documento ? new Date(d.data_documento).toLocaleDateString() : '-'}</strong>
                                                        {d.origem === 'conciliada' && <span title="Conciliada com movimento Revolut" style={{ marginLeft: 6, fontSize: '0.7rem', color: '#166534', fontWeight: 700 }}><OrcamentoIcon name="finance" fallback="🏦" /></span>}
                                                        {/* Mesmo fornecedor, data e valor de outra despesa: gravada, mas a verificar. */}
                                                        {d.verificar_motivo && !d.verificado_em && (
                                                            <span role={user?.role === 'admin' ? 'button' : undefined}
                                                                title={d.verificar_motivo + (user?.role === 'admin' ? ' Clique para confirmar que não é duplicado.' : '')}
                                                                onClick={user?.role === 'admin' ? (async e => {
                                                                    e.stopPropagation();
                                                                    if (!window.confirm(d.verificar_motivo + '\n\nConfirma que não é duplicado?')) return;
                                                                    try {
                                                                        await api.post(`/despesas/${d.id}/verificado`);
                                                                        setDespesas(prev => prev.map(x => x.id === d.id ? { ...x, verificado_em: new Date().toISOString() } : x));
                                                                    } catch (err) { alert(err.response?.data?.error || 'Não foi possível confirmar.'); }
                                                                }) : undefined}
                                                                style={{ display: 'block', marginTop: 3, width: 'fit-content', padding: '1px 6px', borderRadius: 6,
                                                                    background: '#fef3c7', border: '1px solid #f59e0b', color: '#92400e',
                                                                    fontSize: '0.7rem', fontWeight: 700, cursor: user?.role === 'admin' ? 'pointer' : 'default' }}>
                                                                ⚠ A verificar
                                                            </span>
                                                        )}
                                                    </td>
                                                    {/* Escolher não avisa ninguém: junta ao lote. Vale para
                                                        qualquer despesa, mesmo as que o sistema não assinalou —
                                                        quem conhece o terreno sabe coisas que os dados não dizem. */}
                                                    <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                                        {/* Um movimento Revolut sem fatura tem id negativo: não é
                                                            despesa nenhuma, é um movimento à espera de talão. Não há
                                                            classificação para corrigir, logo não há o que avisar. */}
                                                        {d.id < 0 ? <div style={CELULA_CAIXA}><span style={{ color: '#e2e8f0' }}>·</span></div> : (
                                                            /* A caixa fica mesmo depois de avisado: insistir é
                                                               normal, e o envelope ao lado diz quantas vezes já
                                                               se avisou — clica-se para ver as datas. */
                                                            <div style={CELULA_CAIXA}>
                                                                <input type="checkbox" style={CAIXA} checked={escolhidas.has(d.id)}
                                                                    onChange={() => alternarEscolha(d.id)}
                                                                    title="Escolher para enviar mensagem ao colaborador" />
                                                                {suspeitas[d.id]?.enviado_em && (
                                                                    <HistoricoEnvios despesaId={d.id}
                                                                        quantos={suspeitas[d.id].envios || 1} />
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {/* Abastecimento mal lançado: a bomba assinala, o visto
                                                            confirma, o envelope diz que já se avisou e quando.
                                                            Vai a seguir ao nome, na mesma linha, porque é sobre
                                                            aquele comerciante que ela está a falar. */}
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                                            <strong>{d.fornecedor || d.nif_emissor || '—'}</strong>
                                                            <SinalCombustivel s={suspeitas[d.id]} onMudou={fetchData} />
                                                        </span>
                                                        {d.comerciante_cidade && <div style={{ fontSize: '0.72rem', color: '#a0aec0' }}>{d.comerciante_cidade}</div>}
                                                        {d.tipo === 'combustivel' && (
                                                            <div style={{ fontSize: '0.72rem', color: '#c05621' }}><LiquidText>
                                                                ⛽ </LiquidText>{d.veiculo_matricula || d.veiculo_nome || 'viatura'}
                                                                {d.litros != null && ` · ${Number(d.litros).toLocaleString('pt-PT')} L`}
                                                                {d.kms != null && ` · ${Number(d.kms).toLocaleString('pt-PT')} km`}
                                                                {(d.veiculo_alugada || d.veiculo_nome) && ` · ${d.veiculo_alugada ? 'Viatura Alugada' : d.veiculo_nome}`}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ fontSize: '0.85rem', overflowWrap: 'anywhere' }}>{String(d.numero_documento_completo || '').trim() || '—'}</td>
                                                    <td style={{ fontSize: '0.85rem' }}>{d.cartao_nome || '—'}</td>
                                                    <td style={{ fontSize: '0.85rem' }}
                                                        title={d.utilizador_nome ? `Registada na app pela conta de ${d.utilizador_nome}` : 'Sem conta associada'}>
                                                        {d.utilizador_nome || '—'}
                                                    </td>
                                                    <td className="valor-cel">{fmtMoney(d.valor_total)}</td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center' }}>
                                                            {movimentoRevolut ? (
                                                                <>
                                                                    {!verificacao && (
                                                                        <button className="btn-secondary" style={{ padding: '4px 10px', fontSize: '0.78rem', whiteSpace: 'nowrap' }}
                                                                            onClick={(e) => { e.stopPropagation(); anexarFaturaRevolut(d.transacao_id); }} title="Tirar/carregar foto da fatura"><OrcamentoIcon name="camera" fallback="📷" /> Fatura</button>
                                                                    )}
                                                                    <button className="btn-secondary" style={{ padding: '4px 8px', fontSize: '0.78rem' }}
                                                                        onClick={(e) => { e.stopPropagation(); ignorarRevolut(d.transacao_id); }}
                                                                        title={verificacao ? 'Esconder este movimento da lista' : 'Não gera fatura (transferência, taxa…)'}><OrcamentoIcon name="close" fallback="🚫" /></button>
                                                                </>
                                                            ) : (
                                                                <button className="action-btn-delete" onClick={(e) => { e.stopPropagation(); handleDelete(d.id); }} title="Eliminar"><OrcamentoIcon name="trash" fallback="🗑️" /></button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )
                        )}
                        </>
                        )}
                    </>
                )}
            </div>

            {revFatura && (
                <LiquidDialog className="modal-overlay" label="Anexar fatura ao movimento"
                    onClose={() => {if(!revBusy) setRevFatura(null);}}>
                    <form className="modal-ef" style={{maxWidth:460,padding:22}} onSubmit={guardarFaturaRevolut}>
                        <h3>Anexar fatura ao movimento</h3>
                        <p style={{overflowWrap:'anywhere'}}>{revFatura.ficheiro.name}</p>
                        <p>Indique os dados da fatura para evitar registos duplicados.</p>
                        <fieldset disabled={revBusy} style={{border:0,padding:0,display:'grid',gap:12}}>
                            <IdentificacaoFiscal pais={revFatura.pais} nif={revFatura.nif}
                                onChange={({pais,nif})=>setRevFatura({...revFatura,pais,nif})} />
                            <label className="filter-group">Número da fatura
                                <input required maxLength={200} value={revFatura.numero}
                                    onChange={e=>setRevFatura({...revFatura,numero:e.target.value})} /></label>
                            <label className="filter-group">Data de emissão
                                <input required type="date" value={revFatura.data}
                                    onChange={e=>setRevFatura({...revFatura,data:e.target.value})} /></label>
                        </fieldset>
                        {revErro && <p role="alert" style={{color:'#b91c1c'}}>{revErro}</p>}
                        <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginTop:18}}>
                            <button type="button" disabled={revBusy} onClick={()=>setRevFatura(null)}>Cancelar</button>
                            <button type="submit" className="export-btn" disabled={revBusy}>{revBusy?'A anexar…':'Anexar fatura'}</button>
                        </div>
                    </form>
                </LiquidDialog>
            )}
            {/* Detail / Edit Modal */}
            {selectedDespesa && (
                <LiquidDialog onClose={() => { setSelectedDespesa(null); setDetailEditing(false); setNovoComprovativo(null); }} className="modal-overlay" onClick={() => { setSelectedDespesa(null); setDetailEditing(false); setNovoComprovativo(null); }}>
                    <div className="modal-ef" style={{ maxWidth: 620, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-ef-header" style={{ flexShrink: 0 }}>
                            <div>
                                <span className="modal-ef-num">Despesa #{selectedDespesa.id}</span>
                                {selectedDespesa.atcud && !selectedDespesa.atcud.startsWith('TEMP-') && (
                                    <span style={{ marginLeft: 8, fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>{selectedDespesa.atcud}</span>
                                )}
                            </div>
                            <button className="modal-close" onClick={() => { setSelectedDespesa(null); setDetailEditing(false); setNovoComprovativo(null); }}><LiquidText>✕</LiquidText></button>
                        </div>
                        <form onSubmit={handleUpdate} style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                            <div className="modal-form-body" style={{ overflowY: 'auto', flex: 1 }}>
                                <div className="modal-form-grid">
                                    <div className="modal-field">
                                        <label>NIF EMITENTE</label>
                                        {detailEditing ? (
                                            <IdentificacaoFiscal pais={selectedDespesa.pais_nif ?? 'PT'} nif={selectedDespesa.nif_emissor || ''}
                                                onChange={({pais,nif})=>setSelectedDespesa({...selectedDespesa,pais_nif:pais,nif_emissor:nif})} />
                                        ) : (
                                            <p style={{ margin: 0, fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{selectedDespesa.nif_emissor || '-'}</p>
                                        )}
                                    </div>
                                    <div className="modal-field">
                                        <label>VALOR TOTAL</label>
                                        {detailEditing ? (
                                            <input type="number" step="0.01" value={selectedDespesa.valor_total || ''} onChange={(e) => setSelectedDespesa({ ...selectedDespesa, valor_total: e.target.value })} />
                                        ) : (
                                            <p style={{ margin: 0, fontSize: 15, color: '#1e293b', fontWeight: 700 }}>{fmtMoney(selectedDespesa.valor_total)}</p>
                                        )}
                                    </div>
                                    <div className="modal-field">
                                        <label>CARTÃO ASSOCIADO</label>
                                        {detailEditing ? (
                                            <select value={selectedDespesa.cartao_id || ''} onChange={(e) => setSelectedDespesa({ ...selectedDespesa, cartao_id: e.target.value })}>
                                                {cartoes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                                            </select>
                                        ) : (
                                            <p style={{ margin: 0, fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{selectedDespesa.cartao_nome || '-'}</p>
                                        )}
                                    </div>
                                    {cartaoPedeEf(selectedDespesa) && campoTipoDetalhe()}
                                    <div className="modal-field">
                                        <label>{cartaoPedeEf(selectedDespesa) ? 'PROJETO' : 'CÓDIGO DE ALOCAÇÃO'}</label>
                                        {detailEditing ? (() => {
                                            // Como no registo: nos cartões com o visto «Manutenção»
                                            const doCartao = opcoesRegisto?.cartoes?.[selectedDespesa.cartao_id];
                                            const podeEscolher = !!(doCartao?.manutencao && opcoesRegisto?.projeto_empresa);
                                            const eManutencao = podeEscolher && !!selectedDespesa.manutencao;
                                            return (
                                                <>
                                                    {podeEscolher && (
                                                        <EscolhaManutencao compacto manutencao={eManutencao}
                                                            aoEscolher={(v) => {
                                                                if (v === eManutencao) return;
                                                                setSelectedDespesa({ ...selectedDespesa, manutencao: v, id_alocacao: v
                                                                    ? opcoesRegisto.projeto_empresa
                                                                    : (doCartao?.ultimo_projeto || '') });
                                                            }} />
                                                    )}
                                                    {!eManutencao && (
                                                        <input type="text" placeholder="Ex: 11-AA-22" value={selectedDespesa.id_alocacao || ''} onChange={(e) => setSelectedDespesa({ ...selectedDespesa, id_alocacao: e.target.value })} />
                                                    )}
                                                </>
                                            );
                                        })() : cartoes.find(c => String(c.id) === String(selectedDespesa.cartao_id))?.pede_ef ? (
                                            <input type="text" placeholder="Ex: 16087" value={efProjeto.projeto}
                                                onChange={(e) => setEfProjeto(p => ({ ...p, projeto: e.target.value, estado: '', erro: '' }))}
                                                onBlur={() => guardarEfProjeto(efProjeto)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }} />
                                        ) : (
                                            <p style={{ margin: 0, fontSize: 13, color: '#1e293b', fontWeight: 500 }}>
                                                <LiquidText>{selectedDespesa.manutencao ? '🔧 Manutenção' : (selectedDespesa.id_alocacao || '-')}</LiquidText>
                                            </p>
                                        )}
                                    </div>
                                    {/* EF e Projeto: nos cartões que os pedem (o das compras online) */}
                                    {cartoes.find(c => String(c.id) === String(selectedDespesa.cartao_id))?.pede_ef && (
                                        <div className="modal-field">
                                            <label>EF (ENCOMENDA FORNECEDOR)</label>
                                            {detailEditing ? (
                                                <>
                                                    <input type="text" placeholder="Ex: 37290" value={selectedDespesa.ef_numero || ''}
                                                        onChange={(e) => setSelectedDespesa({ ...selectedDespesa, ef_numero: e.target.value })} />
                                                    <small style={{ color: '#718096' }}>Opcional. Com a EF e o projeto vazio, o projeto vem da EF.</small>
                                                </>
                                            ) : (
                                                <>
                                                    <input type="text" placeholder="Ex: 37290" value={efProjeto.ef}
                                                        onChange={(e) => setEfProjeto(p => ({ ...p, ef: e.target.value, estado: '', erro: '' }))}
                                                        onBlur={() => guardarEfProjeto(efProjeto)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }} />
                                                    {selectedDespesa.ef_fornecedor && efProjeto.ef === (selectedDespesa.ef_numero || '') && (
                                                        <small style={{ display: 'block', color: '#4a5568', marginTop: 4 }}>{selectedDespesa.ef_fornecedor}</small>
                                                    )}
                                                    {efProjeto.estado === 'a guardar' && <small style={{ display: 'block', color: '#718096', marginTop: 4 }}>A guardar…</small>}
                                                    {efProjeto.estado === 'guardado' && <small style={{ display: 'block', color: '#2f855a', marginTop: 4 }}><LiquidText>✓ Guardado</LiquidText></small>}
                                                    {efProjeto.erro && <small style={{ display: 'block', color: '#c53030', marginTop: 4 }}>{efProjeto.erro}</small>}
                                                    {!efProjeto.estado && !efProjeto.erro && (
                                                        <small style={{ display: 'block', color: '#718096', marginTop: 4 }}>
                                                            Grava-se ao sair do campo. Com a EF e o projeto vazio, o projeto vem da EF.
                                                        </small>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {!cartaoPedeEf(selectedDespesa) && campoTipoDetalhe()}
                                    {(selectedDespesa.tipo === 'combustivel') && (
                                        <>
                                            <div className="modal-field">
                                                <label>VIATURA</label>
                                                {detailEditing ? (
                                                    <select value={selectedDespesa.veiculo_id || ''} onChange={(e) => setSelectedDespesa({ ...selectedDespesa, veiculo_id: e.target.value })}>
                                                        <option value="">Selecionar viatura</option>
                                                        {veiculos.map(v => <option key={v.id} value={v.id}>{[v.matricula, v.nome].filter(Boolean).join(' · ')}</option>)}
                                                    </select>
                                                ) : (
                                                    <p style={{ margin: 0, fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{[selectedDespesa.veiculo_matricula, selectedDespesa.veiculo_nome].filter(Boolean).join(' · ') || '-'}</p>
                                                )}
                                            </div>
                                            <div className="modal-field">
                                                <label>KMS</label>
                                                {detailEditing ? (
                                                    <input type="number" min="0" step="1" value={selectedDespesa.kms ?? ''} onChange={(e) => setSelectedDespesa({ ...selectedDespesa, kms: e.target.value })} />
                                                ) : (
                                                    <p style={{ margin: 0, fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{selectedDespesa.kms != null ? `${Number(selectedDespesa.kms).toLocaleString('pt-PT')} km` : '-'}</p>
                                                )}
                                            </div>
                                            <div className="modal-field">
                                                <label>LITROS</label>
                                                {detailEditing ? (
                                                    <input type="number" min="0" step="0.01" value={selectedDespesa.litros ?? ''} onChange={(e) => setSelectedDespesa({ ...selectedDespesa, litros: e.target.value })} />
                                                ) : (
                                                    <p style={{ margin: 0, fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{selectedDespesa.litros != null ? `${Number(selectedDespesa.litros).toLocaleString('pt-PT')} L` : '-'}</p>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    <div className="modal-field">
                                        <label>DATA DO DOCUMENTO</label>
                                        {detailEditing ? (
                                            <input type="date" value={selectedDespesa.data_documento ? selectedDespesa.data_documento.substring(0, 10) : ''} onChange={(e) => setSelectedDespesa({ ...selectedDespesa, data_documento: e.target.value })} />
                                        ) : (
                                            <p style={{ margin: 0, fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{new Date(selectedDespesa.data_documento || selectedDespesa.criado_em).toLocaleDateString()}</p>
                                        )}
                                    </div>
                                    <div className="modal-field">
                                        <label>NIF ADQUIRENTE</label>
                                        <p style={{ margin: 0, fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{quemAdquiriu(selectedDespesa.nif_adquirente)}</p>
                                    </div>
                                    <div className="modal-field">
                                        <label>TIPO DE DOCUMENTO</label>
                                        <p style={{ margin: 0, fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{selectedDespesa.tipo_documento || '-'}</p>
                                    </div>
                                    <div className="modal-field full">
                                        <label>ATCUD</label>
                                        {detailEditing ? (
                                            <input type="text" value={selectedDespesa.atcud || ''} onChange={(e) => setSelectedDespesa({ ...selectedDespesa, atcud: e.target.value })} style={{ fontFamily: 'monospace' }} />
                                        ) : (
                                            <p style={{ margin: 0, fontSize: 13, color: '#1e293b', fontFamily: 'monospace', fontWeight: 500 }}>{selectedDespesa.atcud || '-'}</p>
                                        )}
                                    </div>
                                </div>

                                <div style={{ marginTop: 16 }}>
                                    <label style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 6 }}>COMPROVATIVO</label>
                                    {novoComprovativo ? (
                                        <div style={{ background: '#f0fff4', border: '1px solid #c6f6d5', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#276749', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                                            <span style={{ wordBreak: 'break-all' }}>
                                                <LiquidText>{/\.pdf$/i.test(novoComprovativo.name) ? '📄' : '🖼️'}</LiquidText> <strong>{novoComprovativo.name}</strong> — substitui o atual ao guardar
                                            </span>
                                            <button type="button" className="btn-modal-cancel" onClick={() => setNovoComprovativo(null)}>Anular</button>
                                        </div>
                                    ) : selectedDespesa.caminho_foto ? (
                                        <ComprovativoImg despesaId={selectedDespesa.id} versao={selectedDespesa.caminho_foto} style={{ width: '100%', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                                    ) : (
                                        <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>Sem comprovativo anexado.</p>
                                    )}

                                    {detailEditing && (
                                        <>
                                            <input id="trocar-comprovativo" type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                                                onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; if (f) setNovoComprovativo(f); }} />
                                            <button type="button" className="btn-modal-cancel" style={{ marginTop: 8 }}
                                                onClick={() => document.getElementById('trocar-comprovativo').click()}>
                                                <LiquidText>{selectedDespesa.caminho_foto ? '🔄 Substituir comprovativo' : '📎 Anexar comprovativo'}</LiquidText>
                                            </button>
                                        </>
                                    )}
                                </div>

                                <div className="modal-form-actions" style={{ justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button type="button" className="btn-modal-cancel" style={{ color: '#c53030', background: '#fee2e2', borderColor: '#fecaca' }} onClick={() => { handleDelete(selectedDespesa.id); setSelectedDespesa(null); }}><OrcamentoIcon name="trash" fallback="🗑️" /> Apagar</button>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        {detailEditing ? (
                                            <>
                                                <button type="button" className="btn-modal-cancel" onClick={() => { setDetailEditing(false); setNovoComprovativo(null); }}>Cancelar</button>
                                                <button type="submit" className="btn-modal-save">Guardar</button>
                                            </>
                                        ) : (
                                            <button type="button" className="btn-modal-cancel" onClick={() => setDetailEditing(true)}><OrcamentoIcon name="edit" fallback="✏️" /> Editar</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </LiquidDialog>
            )}

            {/* ── QR Scanner camera + erro ── fullscreen */}
            {showScanner && !scanResult && !manualEntry && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#0a0a0a', zIndex: 3000, display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <div style={{ background: 'rgba(0,0,0,0.7)', padding: `max(env(safe-area-inset-top,16px),16px) 20px 14px 20px`, display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                        <button
                            type="button"
                            onClick={stopScanner}
                            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', padding: '8px 12px', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer' }}
                        >
                            ← Voltar
                        </button>
                        <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: 'white', flex: 1 }}><OrcamentoIcon name="camera" fallback="📷" /> Scanner AT</h2>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <div data-liquid-surface="true" className="scanner-modal" style={{ background: 'white', padding: '20px', borderRadius: '16px', width: '100%', maxWidth: '500px' }}>
                        {qrErrorMsg && (
                            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '16px' }}><OrcamentoIcon name="warning" fallback="⚠️" /></div>
                                <h2 style={{ color: '#c53030', marginBottom: '8px' }}>Erro na Leitura</h2>
                                <p style={{ color: '#4a5568', fontSize: '1rem', marginBottom: '30px' }}>{qrErrorMsg}</p>
                                <div data-liquid-actions="true" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <button
                                        type="button"
                                        className="btn-scan"
                                        style={{ width: '100%', padding: '16px', fontSize: '1.05rem' }}
                                        onClick={() => startScanner()}
                                    >
                                        <OrcamentoIcon name="refresh" fallback="🔄" /> Voltar a Tentar QR Code
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-scan"
                                        style={{ width: '100%', padding: '16px', fontSize: '1.05rem', background: '#2b6cb0' }}
                                        onClick={fotografarQr}
                                    >
                                        <OrcamentoIcon name="camera" fallback="📷" /> Fotografar o QR
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-scan"
                                        style={{ width: '100%', padding: '16px', fontSize: '1.05rem', background: '#ed8936' }}
                                        onClick={registoManualDoScanner}
                                    >
                                        <OrcamentoIcon name="edit" fallback="✏️" /> Carregar Manualmente
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-cancel-manual"
                                        style={{ width: '100%', marginTop: '5px' }}
                                        onClick={() => { setQrErrorMsg(''); stopScanner(); }}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* À espera da fotografia tirada com a câmara do telemóvel */}
                        {!qrErrorMsg && qrModoFoto && (
                            <div style={{ textAlign: 'center', padding: '24px 0' }}>
                                <div style={{ fontSize: '2.6rem', marginBottom: '12px' }}><OrcamentoIcon name="camera" fallback="📷" /></div>
                                <h2 style={{ color: '#2d3748', marginBottom: '8px', fontSize: '1.15rem' }}>
                                    {qrLendoFoto ? 'A ler o QR da fotografia…' : 'A aguardar a fotografia…'}
                                </h2>
                                {!qrLendoFoto && (
                                    <>
                                        <p style={{ color: '#4a5568', fontSize: '0.95rem', marginBottom: '24px' }}>
                                            Fotografe o QR de perto, nítido e direito, sem reflexos.
                                        </p>
                                        <div data-liquid-actions="true" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <button type="button" className="btn-scan" style={{ width: '100%', padding: '15px', fontSize: '1rem', background: '#2b6cb0' }} onClick={fotografarQr}>
                                                <OrcamentoIcon name="camera" fallback="📷" /> Fotografar o QR
                                            </button>
                                            <button type="button" className="btn-scan" style={{ width: '100%', padding: '15px', fontSize: '1rem' }} onClick={() => startScanner()}>
                                                <OrcamentoIcon name="refresh" fallback="🔄" /> Voltar à leitura ao vivo
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Leitura ao vivo. O #reader fica sempre montado enquanto
                            o ecrã está aberto (só se esconde): a biblioteca guarda
                            referências aos elementos que lá pôs. */}
                        <div style={{ display: (qrErrorMsg || qrModoFoto) ? 'none' : 'block' }}>
                            {qrDica && (
                                <div style={{ background: '#fffaf0', border: '1px solid #fbd38d', color: '#7b341e', borderRadius: '10px', padding: '10px 12px', fontSize: '0.88rem', marginBottom: '10px', lineHeight: 1.4 }}>
                                    Ainda não leu? Afaste um pouco o telemóvel até o QR ficar nítido{qrZoom ? ', experimente outro zoom' : ''}, evite reflexos ou toque em <strong>Fotografar o QR</strong>.
                                </div>
                            )}
                            <div style={{ position: 'relative', width: '100%', minHeight: '180px', overflow: 'hidden', borderRadius: '12px', background: '#000' }}>
                                <div style={QR_ESCALA_LEITOR > 1 ? { position: 'absolute', top: 0, left: 0, width: `${QR_ESCALA_LEITOR * 100}%`, transform: `scale(${1 / QR_ESCALA_LEITOR})`, transformOrigin: '0 0' } : undefined}>
                                    <div id="reader" ref={ligarReaderQr}></div>
                                </div>
                                {qrZoom && (
                                    <button
                                        type="button"
                                        onClick={trocarZoomQr}
                                        aria-label="Mudar o zoom da câmara"
                                        style={{ position: 'absolute', right: '10px', bottom: '10px', zIndex: 2, background: 'rgba(0,0,0,0.6)', color: 'white', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '20px', padding: '7px 14px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}
                                    >
                                        {`${Math.round(qrZoom.atual * 10) / 10}×`}
                                    </button>
                                )}
                            </div>
                            <p style={{ color: '#4a5568', fontSize: '0.85rem', margin: '10px 0 0', textAlign: 'center', lineHeight: 1.4 }}>
                                Enquadre só o QR da fatura no quadrado. Se ficar desfocado, afaste um pouco o telemóvel{qrZoom ? ' ou mude o zoom' : ''}.
                            </p>
                        </div>
                    </div>
                    </div>
                    {/* Alternativas sempre à mão, sem esperar que a leitura falhe */}
                    {!qrErrorMsg && !qrModoFoto && (
                        <div style={{ flexShrink: 0, display: 'flex', gap: '10px', background: 'rgba(0,0,0,0.7)', padding: '12px 16px max(env(safe-area-inset-bottom, 12px), 12px) 16px' }}>
                            <button type="button" className="btn-scan" style={{ flex: 1, padding: '14px 8px', fontSize: '0.95rem', background: '#2b6cb0' }} onClick={fotografarQr}>
                                <OrcamentoIcon name="camera" fallback="📷" /> Fotografar o QR
                            </button>
                            <button type="button" className="btn-scan" style={{ flex: 1, padding: '14px 8px', fontSize: '0.95rem', background: '#ed8936' }} onClick={registoManualDoScanner}>
                                <OrcamentoIcon name="edit" fallback="✏️" /> Registo manual
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ── Formulário (manual ou resultado QR) ── fullscreen page */}
            {showScanner && (scanResult || manualEntry) && (
                <div className="liquid-capture-form" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#f7fafc', zIndex: 3000, display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>
                    {/* Header */}
                    <div style={{ background: manualEntry ? 'linear-gradient(135deg,#dd6b20,#c05621)' : 'linear-gradient(135deg,#3182ce,#2b6cb0)', color: 'white', padding: `max(env(safe-area-inset-top,16px),16px) 20px 14px 20px`, display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                        <button
                            type="button"
                            onClick={() => { setScanResult(null); setManualEntry(false); setShowScanner(false); setDetectedAtcud(''); setDetectedFornecedor(null); setNifManual(''); setPaisNifManual('PT'); setNumeroManual(''); setQrNif(''); setQrValor(''); setCartaoProvavel(null); setPhoto(null); setPhotoPreview(null); setPhotoPdfNome(null); setParaManutencao(false); }}
                            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', padding: '8px 12px', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                            ← Voltar
                        </button>
                        <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', flex: 1 }}>
                            <LiquidText>{manualEntry ? '✏️ Registo Manual' : '✅ Finalizar Registo'}</LiquidText>
                        </h2>
                    </div>

                    {/* Scrollable form body */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '20px', WebkitOverflowScrolling: 'touch' }}>
                    <form ref={registoDespesaRef} onSubmit={handleSubmit}>

                                {/* Tipo de despesa: geral ou combustível (viatura + kms + litros) */}
                                <div className="form-group">
                                    <label>Tipo de despesa</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {[['', '🧾 Geral'], ['combustivel', '⛽ Combustível']].map(([v, l]) => (
                                            <button key={v || 'geral'} type="button" onClick={() => setTipoDespesa(v)}
                                                style={{ flex: 1, padding: '12px', borderRadius: '10px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
                                                    border: tipoDespesa === v ? '2px solid #3182ce' : '1px solid #e2e8f0',
                                                    background: tipoDespesa === v ? '#ebf8ff' : 'white', color: tipoDespesa === v ? '#2b6cb0' : '#4a5568' }}>
                                                {l}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {tipoDespesa === 'combustivel' && (
                                    <div data-liquid-surface="true" style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px', marginBottom: '14px' }}>
                                        <div className="form-group">
                                            <label htmlFor="viatura-abastecida">Viatura abastecida</label>
                                            <select id="viatura-abastecida" name="veiculo_id" value={novaViatura ? '__nova__' : veiculoId}
                                                onChange={(e) => {
                                                    if (e.target.value === '__nova__') { setNovaViatura({ matricula: '', marca: '', modelo: '' }); return; }
                                                    setNovaViatura(null);
                                                    setVeiculoId(e.target.value);
                                                }} required>
                                                <option value="">Selecionar viatura</option>
                                                {/* Só a frota da casa. Uma alugada não fica na lista: está
                                                    cá duas semanas e ia empilhar-se com as das semanas
                                                    anteriores, até a lista deixar de se poder usar. Quem
                                                    abastece uma alugada volta a escrever a matrícula — é pelo
                                                    que ela fica escrita que o registo se agrupa depois.
                                                    A que se acabou de escrever aparece à mesma, senão o campo
                                                    ficava em branco depois de a guardar. */}
                                                {veiculos.filter(v => !v.alugada || String(v.id) === String(veiculoId))
                                                    .map(v => <option key={v.id} value={v.id}>
                                                        {[v.matricula, v.nome].filter(Boolean).join(' · ')}{v.alugada ? ' · alugada' : ''}
                                                    </option>)}
                                                <option value="__nova__">➕ Acrescentar viatura alugada…</option>
                                            </select>
                                            {veiculos.length === 0 && <small style={{ color: '#c05621' }}>Sem viaturas disponíveis — a sua conta não tem acesso a Veículos.</small>}
                                            {novaViatura && (
                                                <div data-liquid-surface="true" style={{ marginTop: 10, padding: 12, borderRadius: 10,
                                                    background: '#fff', border: '1px solid #cbd5e1' }}>
                                                    <strong style={{ fontSize: '0.85rem' }}>Viatura alugada</strong>
                                                    <small style={{ display: 'block', color: '#4a5568', marginTop: 6 }}>A viatura fica guardada ao confirmar o registo da despesa.</small>
                                                    {/* Só a matrícula: marca e modelo não entram em conta nenhuma. */}
                                                    <div style={{ marginTop: 8 }}>
                                                        <label style={{ fontSize: '0.8rem', color: '#4a5568' }}>Matrícula</label>
                                                        <input name="matricula_alugada" value={novaViatura.matricula || ''} placeholder="AB-12-CD"
                                                            inputMode="text" autoCapitalize="characters" maxLength={8}
                                                            onChange={(e) => setNovaViatura(v => ({
                                                                ...v, matricula: formatarMatricula(e.target.value), erro: '',
                                                            }))}
                                                            style={{ width: '100%', padding: '10px', borderRadius: 8,
                                                                border: '1px solid #e2e8f0', fontSize: '1.1rem',
                                                                letterSpacing: '.08em', textAlign: 'center',
                                                                boxSizing: 'border-box' }} />
                                                    </div>
                                                    {novaViatura.erro && <small style={{ color: '#c53030', display: 'block', marginTop: 6 }}>{novaViatura.erro}</small>}
                                                    <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                                        <button type="button" className="btn-primary" style={{ flex: 1 }}
                                                            disabled={aGuardarViatura} onClick={guardarViatura}>
                                                            {aGuardarViatura ? 'A guardar…' : 'Guardar viatura'}
                                                        </button>
                                                        <button type="button" className="btn-secondary"
                                                            onClick={() => setNovaViatura(null)}>Cancelar</button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                            <div className="form-group" style={{ marginBottom: 0 }}>
                                                <label>Kms (conta-quilómetros)</label>
                                                <input type="number" inputMode="numeric" min="0" step="1" placeholder="Ex: 123456" value={kmsAbastecimento}
                                                    onChange={(e) => setKmsAbastecimento(e.target.value)} required
                                                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '1rem', boxSizing: 'border-box' }} />
                                            </div>
                                            <div className="form-group" style={{ marginBottom: 0 }}>
                                                <label>Litros</label>
                                                <input type="number" inputMode="decimal" min="0" step="0.01" placeholder="Ex: 45.30" value={litros}
                                                    onChange={(e) => setLitros(e.target.value)} required
                                                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '1rem', boxSizing: 'border-box' }} />
                                            </div>
                                        </div>
                                        {(() => { const v = veiculos.find(x => String(x.id) === String(veiculoId)); return v?.ultimo_km
                                            ? <small style={{ color: '#718096', display: 'block', marginTop: '8px' }}>Última leitura desta viatura: {Number(v.ultimo_km).toLocaleString('pt-PT')} km</small>
                                            : null; })()}
                                    </div>
                                )}

                                {/* QR Code Data Preview */}
                                {!manualEntry && qrNif && qrValor && (
                                    <div className="qr-preview-box">
                                        <div className="qr-preview-header">Dados lidos do QR Code</div>
                                        <div className="qr-preview-grid">
                                            <div className="qr-preview-item">
                                                <span className="qr-preview-label">NIF Emissor</span>
                                                <span className="qr-preview-value">{qrNif}</span>
                                            </div>
                                            <div className="qr-preview-item">
                                                <span className="qr-preview-label">Valor Total</span>
                                                <span className="qr-preview-value qr-preview-valor">{qrValor}€</span>
                                            </div>
                                        </div>
                                        <small style={{ color: '#718096', display: 'block', marginTop: '8px' }}>Verifique se os dados correspondem à fatura.</small>
                                    </div>
                                )}

                                {manualEntry && (
                                    <div className="form-group">
                                        <label>Valor Total (€)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={valorTotal}
                                            onChange={(e) => setValorTotal(e.target.value)}
                                            required
                                            style={{ fontSize: '1.5rem', fontWeight: 'bold' }}
                                        />
                                    </div>
                                )}

                                {manualEntry && (
                                    <div className="form-group">
                                        <label htmlFor="data-fatura">Data da fatura (obrigatória)</label>
                                        <input
                                            id="data-fatura"
                                            type="date"
                                            value={dataManual}
                                            min={limiteAntigoISO()}
                                            max={hojeISO()}
                                            onChange={(e) => { setDataManual(e.target.value); setDataConfirmada(false); }}
                                            required
                                            style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '1rem', boxSizing: 'border-box' }}
                                        />
                                        {/* Dizer o mês em que a despesa vai cair, e dizê-lo alto
                                            quando não é o mês corrente: é aí que o erro acontece e
                                            é aí que ninguém dá por ele até faltar no fecho. */}
                                        {/* O que se leu no talão. Aparece sempre que difere do que
                                            está escrito — é o confronto que apanha a data deixada
                                            por distração. */}
                                        {dataOcr && dataOcr !== dataManual && (
                                            <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fcd34d', fontSize: 13, color: '#92400e' }}>
                                                No talão lê-se <strong>{new Date(`${dataOcr}T00:00:00`).toLocaleDateString('pt-PT')}</strong>.
                                                <button type="button"
                                                    onClick={() => { setDataManual(dataOcr); setDataConfirmada(false); }}
                                                    style={{ marginLeft: 10, padding: '4px 10px', borderRadius: 6, border: '1px solid #92400e', background: 'white', color: '#92400e', cursor: 'pointer', fontWeight: 600 }}>
                                                    usar esta data
                                                </button>
                                            </div>
                                        )}
                                        {!dataManual && (
                                            <small style={{ color: '#718096', display: 'block', marginTop: '8px' }}>
                                                Escreva a data que está na fatura.
                                            </small>
                                        )}
                                        {dataManual && (dataManual.substring(0, 7) === hojeISO().substring(0, 7) ? (
                                            <small style={{ color: '#718096', display: 'block', marginTop: '8px' }}>
                                                Conta em {mesPorExtenso(dataManual)}.
                                            </small>
                                        ) : (
                                            <small style={{ color: '#9a3412', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', display: 'block', marginTop: '8px', padding: '8px 10px' }}>
                                                Esta fatura vai contar em <strong>{mesPorExtenso(dataManual)}</strong>, não no mês corrente.
                                            </small>
                                        ))}
                                    </div>
                                )}

                                {manualEntry && (
                                    <div className="form-group">
                                        <IdentificacaoFiscal pais={paisNifManual} nif={nifManual}
                                            onChange={({pais,nif})=>{setPaisNifManual(pais);setNifManual(nif);}} />
                                    </div>
                                )}

                                {manualEntry && <div className="form-group">
                                    <label>Número completo da fatura (obrigatório)</label>
                                    <input type="text" required maxLength={255} value={numeroManual} onChange={e => setNumeroManual(e.target.value)} placeholder="Ex: FT A2026/1234" />
                                    <small>Copie o número e a série exatamente como aparecem no documento.</small>
                                </div>}

                                {manualEntry && (
                                    <div className="form-group">
                                        <label>Código ATCUD (opcional)</label>
                                        <input
                                            type="text"
                                            placeholder="Ex: JF3H639K-150457"
                                            value={detectedAtcud}
                                            onChange={(e) => setDetectedAtcud(e.target.value)}
                                            style={{ fontFamily: 'monospace', width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '1rem', boxSizing: 'border-box' }}
                                        />
                                        <small style={{ color: '#718096' }}>O OCR tentará detetar isto automaticamente na foto.</small>
                                    </div>
                                )}

                                <div className="form-group"><label>Cartão</label>
                                    <select value={selectedCartao} onChange={(e) => setSelectedCartao(e.target.value)} required>
                                        <option value="">Selecionar Cartão</option>
                                        {cartoes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                                    </select>
                                    {cartaoProvavel && String(cartaoProvavel.cartao_id) !== String(selectedCartao) && (
                                        <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 10,
                                            background: '#fffbeb', border: '1px solid #fcd34d', color: '#78350f', fontSize: '0.88rem' }}>
                                            <strong>Pagaste com o {cartaoProvavel.cartao_nome}?</strong>
                                            <div style={{ marginTop: 3, fontSize: '0.82rem' }}>
                                                O banco tem um pagamento de {Number(cartaoProvavel.valor).toFixed(2).replace('.', ',')} €
                                                {' '}a {String(cartaoProvavel.dia).split('-').reverse().join('/')}
                                                {cartaoProvavel.comerciante ? ` em ${cartaoProvavel.comerciante}` : ''} nesse cartão.
                                            </div>
                                            <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                                <button type="button" className="btn-primary" style={{ flex: 1, padding: '8px' }}
                                                    onClick={() => setSelectedCartao(String(cartaoProvavel.cartao_id))}>
                                                    Sim, usar esse cartão
                                                </button>
                                                <button type="button" className="btn-secondary" style={{ padding: '8px 14px' }}
                                                    onClick={() => setCartaoProvavel(null)}>
                                                    Não
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="form-group"><label>{isMobileUser ? 'Foto da Fatura' : 'Comprovativo (foto ou PDF)'}</label>
                                    <div className="custom-camera-input" onClick={() => document.getElementById('camera-input').click()}>
                                        <div className="camera-preview-box" style={{ position: 'relative' }}>
                                            {photoPdfNome ? (
                                                <div className="camera-placeholder"><OrcamentoIcon name="document" fallback="📄" />
                                                    <p style={{ wordBreak: 'break-all' }}>{photoPdfNome}</p>
                                                    <small style={{ color: '#718096' }}>PDF anexado — clique para trocar</small>
                                                </div>
                                            ) : photoPreview ? <img src={photoPreview} alt="Preview" className="preview-photo" /> : (
                                                <div className="camera-placeholder"><LiquidText>{isMobileUser ? '📷' : '📎'}</LiquidText>
                                                    <p>{isMobileUser ? 'Tirar Foto' : 'Escolher ficheiro'}</p>
                                                </div>
                                            )}
                                            {isAnalyzing && (
                                                <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.8)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' }}>
                                                    <div className="spinner" style={{ border: '3px solid #f3f3f3', borderTop: '3px solid #3182ce', borderRadius: '50%', width: '30px', height: '30px', animation: 'spin 1s linear infinite' }}></div>
                                                    <p style={{ marginTop: '10px', fontSize: '0.8rem', fontWeight: '600', color: '#2b6cb0' }}>A analisar fatura...</p>
                                                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {/* No telemóvel abre a câmara; no PC aceita imagem ou PDF do disco */}
                                    <input id="camera-input" type="file" accept="image/*,application/pdf"
                                        {...(isMobileUser ? { capture: 'environment' } : {})}
                                        onChange={handlePhotoChange} style={{ display: 'none' }} />
                                    {detectedAtcud && (
                                        <div style={{ marginTop: '8px', background: '#f0fff4', border: '1px solid #c6f6d5', padding: '8px', borderRadius: '8px', fontSize: '0.85rem' }}>
                                            <span style={{ color: '#2f855a', fontWeight: '700' }}><LiquidText>✓ ATCUD detetado:</LiquidText></span> <code style={{ color: '#276749' }}>{detectedAtcud}</code>
                                            {detectedFornecedor && <div style={{ marginTop: '4px', color: '#276749' }}>Fornecedor reconhecido: <strong>{detectedFornecedor}</strong></div>}
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>{temManutencao ? 'Para onde vai esta compra?' : 'ID Associado / Obra'}</label>
                                    {/* Quem tem projeto de manutenção escolhe aqui, compra a compra;
                                        a seguinte volta a abrir em «Projeto / Obra». */}
                                    {temManutencao && (
                                        <EscolhaManutencao manutencao={paraManutencao} aoEscolher={setParaManutencao} />
                                    )}
                                    {!(temManutencao && paraManutencao) && (
                                        <input
                                            type="text"
                                            placeholder="Ex: 123 (opcional)"
                                            value={idAlocacao}
                                            onChange={(e) => setIdAlocacao(e.target.value)}
                                            style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '1rem', boxSizing: 'border-box' }}
                                        />
                                    )}
                                </div>

                                {pedirDataAgora && (
                                    <div style={{ margin: '0 0 14px', padding: '16px', borderRadius: 12, background: '#fef2f2', border: '2px solid #dc2626' }}>
                                        <div style={{ fontWeight: 800, color: '#991b1b', fontSize: '1rem', marginBottom: 8 }}>
                                            ATENÇÃO
                                        </div>
                                        <div style={{ color: '#7f1d1d', fontSize: 14, marginBottom: 4 }}>
                                            Confirma que a data inserida é a data da fatura?{' '}
                                            <strong>(tem que ser essa a data inserida!)</strong>
                                        </div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#7f1d1d', margin: '6px 0' }}>
                                            {dataManual ? new Date(`${dataManual}T00:00:00`).toLocaleDateString('pt-PT') : '—'}
                                        </div>
                                        <div style={{ color: '#7f1d1d', fontSize: 13, marginBottom: 12 }}>
                                            Esta despesa vai contar em <strong>{mesPorExtenso(dataManual)}</strong>.
                                        </div>
                                        {dataOcr && dataOcr !== dataManual && (
                                            <div data-liquid-surface="true" style={{ background: 'white', border: '1px solid #dc2626', borderRadius: 8, padding: '8px 10px', marginBottom: 12, fontSize: 13, color: '#991b1b' }}>
                                                No talão lê-se <strong>{new Date(`${dataOcr}T00:00:00`).toLocaleDateString('pt-PT')}</strong>.
                                            </div>
                                        )}
                                        {/* O banco. Nos cartões Revolut os movimentos entram pela API
                                            todos os dias: se não há nenhum deste valor por estas datas,
                                            ou a data está errada ou a compra não foi feita com este
                                            cartão. */}
                                        {confereBanco?.aplicavel && confereBanco.no_mes === false && confereBanco.candidatos?.length > 0 && (
                                            <div data-liquid-surface="true" style={{ background: 'white', border: '1px solid #dc2626', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 13, color: '#991b1b' }}>
                                                Não há nenhum pagamento de <strong>{fmtMoney(valorTotal)}</strong> neste cartão
                                                por estas datas. Há {confereBanco.candidatos.length === 1 ? 'um' : 'estes'}:
                                                {confereBanco.candidatos.map(c => (
                                                    <div key={`${c.data}-${c.valor}`} style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <span>
                                                            <strong>{new Date(`${String(c.data).substring(0, 10)}T00:00:00`).toLocaleDateString('pt-PT')}</strong>
                                                            {' · '}{c.onde || 'sem descrição'}{' · '}{fmtMoney(c.valor)}
                                                        </span>
                                                        <button type="button"
                                                            onClick={() => { setDataManual(String(c.data).substring(0, 10)); setConfereBanco(null); setPedirDataAgora(false); }}
                                                            style={{ padding: '3px 9px', borderRadius: 6, border: '1px solid #991b1b', background: 'white', color: '#991b1b', cursor: 'pointer', fontWeight: 600 }}>
                                                            usar esta data
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {confereBanco?.aplicavel && confereBanco.no_mes === false && !confereBanco.candidatos?.length && (
                                            <div data-liquid-surface="true" style={{ background: 'white', border: '1px solid #dc2626', borderRadius: 8, padding: '8px 10px', marginBottom: 12, fontSize: 13, color: '#991b1b' }}>
                                                Não há nenhum pagamento de <strong>{fmtMoney(valorTotal)}</strong> neste cartão,
                                                em data nenhuma. Confirma o cartão e o valor.
                                            </div>
                                        )}
                                        <div data-liquid-actions="true" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                            <button type="button"
                                                onClick={() => { setDataConfirmada(true); setPedirDataAgora(false); submitDespesa(false); }}
                                                style={{ flex: 1, minWidth: 140, padding: '12px', borderRadius: 8, border: 'none', background: '#16a34a', color: 'white', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}>
                                                SIM — é esta a data
                                            </button>
                                            <button type="button"
                                                onClick={() => { setPedirDataAgora(false); setTimeout(() => { const c = document.querySelector('input[type="date"]'); if (c) { c.focus(); c.scrollIntoView({ block: 'center' }); } }, 50); }}
                                                style={{ flex: 1, minWidth: 140, padding: '12px', borderRadius: 8, border: '2px solid #dc2626', background: 'white', color: '#991b1b', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}>
                                                NÃO — inserir a data da fatura
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <button type="submit" className="btn-scan" style={{ width: '100%' }} disabled={submitting || aGuardarViatura}>{submitting ? 'A verificar comprovativo e registar…' : 'Confirmar e Registar'}</button>
                            </form>
                    </div>
                </div>
            )}
            {/* Escolher o que se vai exportar. Abre com o mês, o ano e o cartão
                do ecrã, para o caso normal ser só confirmar. */}
            {exportar && (() => {
                const nomeMes = (m) => new Date(0, m - 1).toLocaleString('pt-PT', { month: 'long' });
                const cartao = cartoes.find(c => String(c.id) === String(exportar.cartao_id));
                const eZip = exportar.tipo === 'zip';
                const confirmar = () => {
                    const escolha = { mes: exportar.mes, ano: exportar.ano, cartao_id: exportar.cartao_id, cartao_ids: exportar.cartao_ids };
                    setExportar(null);
                    if (eZip) handleExportZip(escolha); else handleExportExcel(escolha);
                };
                return (
                    <LiquidDialog onClose={() => setExportar(null)} className="modal-overlay" onClick={() => setExportar(null)}>
                        {/* Mesma caixa do detalhe da despesa (.modal-ef), que é a
                            que esta página já usa e traz o fundo, o arredondado e a
                            sombra — .modal-content vive na CSS de outras páginas. */}
                        <div className="modal-ef" style={{ maxWidth: 460, padding: '20px 22px' }} onClick={e => e.stopPropagation()}>
                            <h3 style={{ marginTop: 0 }}><LiquidText>{eZip ? '📦 ZIP de um mês' : '📊 Excel de um mês'}</LiquidText></h3>
                            <p style={{ color: '#4a5568', fontSize: '0.9rem', marginTop: 0 }}>
                                {eZip
                                    ? 'Leva as fotografias e o Excel do mês inteiro. Pode demorar alguns minutos.'
                                    : 'Leva todas as despesas do mês inteiro.'}
                                {' '}Os filtros da lista (fornecedor e funis das colunas) não se aplicam.
                            </p>
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                <div className="filter-group" style={{ flex: '1 1 140px' }}>
                                    <label>Mês</label>
                                    <select value={exportar.mes} onChange={e => setExportar(x => ({ ...x, mes: Number(e.target.value) }))}>
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                            <option key={m} value={m}>{nomeMes(m)}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-group" style={{ flex: '1 1 100px' }}>
                                    <label>Ano</label>
                                    <select value={exportar.ano} onChange={e => setExportar(x => ({ ...x, ano: Number(e.target.value) }))}>
                                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-group" style={{ flex: '1 1 100%' }}>
                                    <label>{eZip ? 'Cartões' : 'Cartão'}</label>
                                    {eZip ? (
                                        <div>
                                            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                <button type="button" className="btn-secondary" onClick={() => setExportar(x => ({ ...x, cartao_ids: cartoes.map(c => String(c.id)) }))}>Selecionar todos</button>
                                                <button type="button" className="btn-secondary" onClick={() => setExportar(x => ({ ...x, cartao_ids: [] }))}>Limpar</button>
                                            </div>
                                            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #c6d8e8', borderRadius: 8, padding: 8 }}>
                                                {cartoes.map(c => (
                                                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', textTransform: 'none', cursor: 'pointer' }}>
                                                        <input type="checkbox" style={{ width: 'auto', margin: 0 }}
                                                            checked={(exportar.cartao_ids || []).includes(String(c.id))}
                                                            onChange={e => { const checked = e.target.checked; setExportar(x => ({ ...x, cartao_ids: checked ? [...(x.cartao_ids || []), String(c.id)] : x.cartao_ids.filter(id => id !== String(c.id)) })); }} />
                                                        <span>{c.nome} {c.numero_mascarado ? `(${c.numero_mascarado})` : ''}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (

                                    <select value={exportar.cartao_id || ''} onChange={e => setExportar(x => ({ ...x, cartao_id: e.target.value }))}>
                                        <option value="">Todos os cartões</option>
                                        {cartoes.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.nome} {c.numero_mascarado ? `(${c.numero_mascarado})` : ''}
                                            </option>
                                        ))}
                                    </select>

                                    )}
                                </div>
                            </div>
                            <p style={{ color: '#2d3748', fontSize: '0.88rem', marginBottom: 18 }}>
                                Vai levar <strong>{nomeMes(exportar.mes)} de {exportar.ano}</strong>
                                {eZip ? <> de <strong>{exportar.cartao_ids?.length || 0} cartão(ões) selecionado(s)</strong></> : cartao ? <> do cartão <strong>{cartao.nome}</strong></> : <> de <strong>todos os cartões</strong></>}.
                            </p>
                            <div data-liquid-actions="true" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                                <button className="btn-secondary" onClick={() => setExportar(null)}>Cancelar</button>
                                <button className="btn-primary" disabled={eZip && !exportar.cartao_ids?.length} onClick={confirmar}>
                                    {eZip ? 'Preparar ZIP' : 'Descarregar Excel'}
                                </button>
                            </div>
                        </div>
                    </LiquidDialog>
                );
            })()}

            {showCardManager && <CardManagerModal onClose={() => setShowCardManager(false)} />}

            {/* Cropper Modal */}
            {showCropper && cropImageSrc && (
                <div className="scanner-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.95)', zIndex: 4000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
                    <div data-liquid-surface="true" style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '500px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, color: '#2d3748' }}>Ajustar Foto</h3>
                            <button onClick={handleCropCancel} style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer', color: '#718096' }}>×</button>
                        </div>
                        <div style={{ flex: 1, overflow: 'hidden', maxHeight: '60vh', background: '#1a202c' }}>
                            <img
                                ref={cropImageRef}
                                src={cropImageSrc}
                                alt="Crop"
                                style={{ display: 'block', maxWidth: '100%' }}
                                onLoad={initCropper}
                            />
                        </div>
                        <div data-liquid-actions="true" style={{ padding: '16px 20px', display: 'flex', gap: '10px', borderTop: '1px solid #e2e8f0' }}>
                            <button
                                type="button"
                                onClick={handleCropConfirm}
                                className="btn-scan"
                                style={{ flex: 1 }}
                            >
                                Confirmar Recorte
                            </button>
                            <button
                                type="button"
                                onClick={handleCropCancel}
                                className="btn-cancel-manual"
                                style={{ flex: 0 }}
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {resultadoRegisto && (
                <LiquidDialog className="modal-overlay despesa-resultado-overlay" label="Resultado do registo">
                    <section className={'despesa-resultado despesa-resultado-' + resultadoRegisto.tipo} role={resultadoRegisto.tipo === 'sucesso' ? 'status' : 'alert'}>
                        <h2>{resultadoRegisto.tipo === 'sucesso' ? 'Despesa registada com sucesso' : resultadoRegisto.tipo === 'erro' ? 'Registo não efetuado' : 'Registo por confirmar'}</h2>
                        {resultadoRegisto.tipo === 'sucesso' ? <>
                            <p>A gravação foi confirmada pelo servidor.</p>
                            <dl>
                                <dt>Referência</dt><dd>#{resultadoRegisto.despesa.id}</dd>
                                <dt>Data da despesa</dt><dd>{resultadoRegisto.despesa.data_documento ? new Date(resultadoRegisto.despesa.data_documento).toLocaleDateString('pt-PT') : '—'}</dd>
                                <dt>Valor</dt><dd>{fmtMoney(resultadoRegisto.despesa.valor_total)}</dd>
                            </dl>
                            <p>A despesa pertence ao mês da data indicada. Se não aparecer na lista, verifique o mês, o cartão e os filtros selecionados.</p>
                            {resultadoRegisto.despesa.aviso_kms && <p className="despesa-resultado-aviso">Kms não guardados na viatura: {resultadoRegisto.despesa.aviso_kms}</p>}
                            {resultadoRegisto.avisoLista && <p>A lista não foi atualizada. A despesa já está guardada; não volte a registá-la.</p>}
                        </> : <><p>{resultadoRegisto.mensagem}</p><p>Os dados e o documento foram mantidos no formulário.</p></>}
                        <button type="button" className="btn-scan" onClick={() => setResultadoRegisto(null)}>
                            {resultadoRegisto.tipo === 'sucesso' ? 'Concluir' : 'Voltar ao formulário'}
                        </button>
                    </section>
                </LiquidDialog>
            )}
        </div>
    );
};

export default Despesas;
